import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
    AppBar, Container, Box, Typography, Button, IconButton, Divider, List, ListItem, ListItemIcon, ListItemText,
    CssBaseline, Toolbar, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
    Snackbar, Alert, FormControl, InputLabel, Select, MenuItem, TextField, CircularProgress, Tab, Tabs,
    Paper, InputBase, FormControlLabel, Switch, Grid, Collapse, Chip, Badge, Drawer, ListItemButton,
    ThemeProvider, Autocomplete, Popper, Fade, Checkbox, FormGroup, RadioGroup, Radio, FormLabel,
    Stack, InputAdornment, LinearProgress, Menu
} from '@mui/material';
import {
    Search as SearchIcon, Info as InfoIcon, Menu as MenuIcon, MenuOpen as MenuOpenIcon,
    Refresh as RefreshIcon, FileDownload as FileDownloadIcon, Close as CloseIcon, CheckCircle as CheckCircleIcon,
    Error as ErrorIcon, ExpandLess as ExpandLessIcon, ExpandMore as ExpandMoreIcon, SearchOff as SearchOffIcon,
    Description as DescriptionIcon, Person as PersonIcon, Folder as FolderIcon, AccountTree as AccountTreeIcon,
    InsertDriveFile as FileIcon, ChatBubbleOutline as ChatBubbleOutlineIcon, UploadFile as UploadFileIcon,
    Transform as TransformIcon, Public as PublicIcon, HelpOutline as HelpOutlineIcon,
    Flight as FlightIcon, Assignment as AssignmentIcon, Report as ReportIcon, BusinessCenter as BusinessCenterIcon,
    Warning as WarningIcon, AutoFixHigh as AutoFixHighIcon, School as SchoolIcon, Event as EventIcon,
    TrendingUp as TrendingUpIcon,
    Explore as ExploreIcon,
    Insights as InsightsIcon,
    Payments as PaymentsIcon,
    GridOn as GridOnIcon
} from '@mui/icons-material';
import axios from 'axios';
import { checkStatus } from './services/api';
import { styled, createTheme, Theme } from '@mui/material/styles';
import { theme } from './theme';
import logo from './assets/logo.png';
import DOMPurify from 'dompurify';
import ExcelJS from 'exceljs';
import { useNavigate, useLocation, Routes, Route, Outlet, Navigate, Link } from 'react-router-dom';
import ChatPage from './ChatPage';
import DocumentChatPage from './DocumentChatPage';
import CVConverterPage from './pages/CVConverterPage';
import CVCreatorPage from './pages/CVCreatorPage';
import FCDOCountryRiskPage from './pages/FCDOCountryRiskPage';
import TravelFormPage from './pages/TravelFormPage';
import TravelListPage from './pages/TravelListPage';
import TravelDashboardPage from './pages/TravelDashboardPage';
import TravelResourcesPage from './pages/TravelResourcesPage';
import TravelOperationsPage from './pages/TravelOperationsPage';
import TravelCalendarPage from './pages/TravelCalendarPage';
import IncidentReportPage from './pages/IncidentReportPage';
import IncidentReportListPage from './pages/IncidentReportListPage';
import CrisisLogPage from './pages/CrisisLogPage';
import EOTAnonymousMessagePage from './pages/EOTAnonymousMessagePage';
import WhistleblowerFormPage from './pages/WhistleblowerFormPage';
import EventFormPage from './pages/EventFormPage';
import EventsAdminPage from './pages/EventsAdminPage';
import BusinessCaseFormPage from './pages/BusinessCaseFormPage';
import MyBusinessCasesPage from './pages/MyBusinessCasesPage';
import BusinessCaseAdminPage from './pages/BusinessCaseAdminPage';
import PPDFormPage from './pages/PPDFormPage';
import MyPPDPage from './pages/MyPPDPage';
import PPDAdminPage from './pages/PPDAdminPage';
import MethodologyBuilderPage from './pages/MethodologyBuilderPage';
import MethodologyAssessmentPage from './pages/MethodologyAssessmentPage';
import ProposalBuilderPage from './pages/ProposalBuilderPage';
import ProposalAssistantPage from './pages/ProposalAssistantPage';
import CVMatchPage from './pages/CVMatchPageV2';
import TorCVSearchPage from './pages/TorCVSearchPageV2';
import TorCriteriaPage from './pages/TorCriteriaPage';
import FindSimilarCVsPage from './pages/FindSimilarCVsPage';
import SkillsMatrixPage from './pages/SkillsMatrixPage';
import CompassTestPage from './pages/CompassTestPage';
import CompassDashboardPage from './pages/CompassDashboardPage';
import ProjectInsightsPage from './pages/ProjectInsightsPage';
import CostEstimatorPage from './pages/CostEstimatorPage';
import LoadingIndicator from './components/LoadingIndicator';
import ReindexProgress from './components/ReindexProgress';
import DocumentViewer from './components/DocumentViewer';

// Admin-only section component - Merged permissions from Business Case Admin + Travel Admin + PPD Admin
const AdminOnlySection: React.FC<{ userEmail: string | null; children: React.ReactNode }> = ({ userEmail, children }) => {
    const ADMIN_EMAILS = [
        'si@iodparc.com',
        'joanne@iodparc.com',
        'jonny@iodparc.com',
        'giulia@iodparc.com',
        'diane@iodparc.com',
        'pam@iodparc.com',
        'sadie@iodparc.com',
        'hatty@iodparc.com',
        'mark@iodparc.com',
        'victoria@iodparc.com'
    ];

    if (userEmail && ADMIN_EMAILS.includes(userEmail.toLowerCase())) {
        return <>{children}</>;
    }

    return null;
};

// Business Case Admin-only section component
const BusinessCaseAdminOnlySection: React.FC<{ userEmail: string | null; children: React.ReactNode }> = ({ userEmail, children }) => {
    const BUSINESS_CASE_ADMIN_EMAILS = [
        'si@iodparc.com',
        'joanne@iodparc.com',
        'jonny@iodparc.com',
        'giulia@iodparc.com'
    ];

    if (userEmail && BUSINESS_CASE_ADMIN_EMAILS.includes(userEmail.toLowerCase())) {
        return <>{children}</>;
    }

    return null;
};

// Events Admin-only section component
const EventsAdminOnlySection: React.FC<{ userEmail: string | null; children: React.ReactNode }> = ({ userEmail, children }) => {
    const EVENTS_ADMIN_EMAILS = [
        'si@iodparc.com',
        'joanne@iodparc.com',
        'pam@iodparc.com',
        'naomi@iodparc.com',
        'ima@iodparc.com',
        'roeland@iodparc.com',
        'sadie@iodparc.com',
        'erica@iodparc.com',
        'enrique@iodparc.com',
        'karolina@iodparc.com',
        'sarah@iodparc.com',
        'hatty@iodparc.com',
        'victoria@iodparc.com'
    ];

    if (userEmail && EVENTS_ADMIN_EMAILS.includes(userEmail.toLowerCase())) {
        return <>{children}</>;
    }

    return null;
};

// Travel Admin-only section component (for Travel Dashboard and Travel Operations)
const TravelAdminOnlySection: React.FC<{ userEmail: string | null; children: React.ReactNode }> = ({ userEmail, children }) => {
    const TRAVEL_ADMIN_EMAILS = [
        'si@iodparc.com',
        'diane@iodparc.com',
        'pam@iodparc.com',
        'sadie@iodparc.com',
        'hatty@iodparc.com'
    ];

    if (userEmail && TRAVEL_ADMIN_EMAILS.includes(userEmail.toLowerCase())) {
        return <>{children}</>;
    }

    return null;
};

// PPD Admin-only section component (for PPD Admin)
const PPDAdminOnlySection: React.FC<{ userEmail: string | null; children: React.ReactNode }> = ({ userEmail, children }) => {
    const PPD_ADMIN_EMAILS = [
        'si@iodparc.com',
        'mark@iodparc.com',
        'hatty@iodparc.com',
        'diane@iodparc.com',
        'victoria@iodparc.com',
        'sadie@iodparc.com'
    ];

    if (userEmail && PPD_ADMIN_EMAILS.includes(userEmail.toLowerCase())) {
        return <>{children}</>;
    }

    return null;
};

// Dev-only section component (for features still in development - si@iodparc.com only)
const DevOnlySection: React.FC<{ userEmail: string | null; children: React.ReactNode }> = ({ userEmail, children }) => {
    const DEV_EMAILS = [
        'si@iodparc.com'
    ];

    if (userEmail && DEV_EMAILS.includes(userEmail.toLowerCase())) {
        return <>{children}</>;
    }

    return null;
};

// Super Admin-only section component.
// Only the super admin sees the full navigation (Search/Analysis, Projects, Bid Support).
// Everyone else (including feature admins) sees only Travel, Forms and Admin.
const SuperAdminOnlySection: React.FC<{ userEmail: string | null; children: React.ReactNode }> = ({ userEmail, children }) => {
    const SUPER_ADMIN_EMAILS = [
        'si@iodparc.com'
    ];

    if (userEmail && SUPER_ADMIN_EMAILS.includes(userEmail.toLowerCase())) {
        return <>{children}</>;
    }

    return null;
};

// Helper function to get user email from authentication
const getUserEmail = (): string | null => {
    // This will be implemented in the component where we have access to auth state
    return null;
};

interface Document {
    id: string;
    filename: string;
    title: string;
    processingDate: string;
}

interface Match {
    text: string;
    context: string;
    matchType: 'exact' | 'partial';
    position: number;
    section_type?: string;
}

interface TermMatch {
    term: string;
    matches: Match[];
    hitCount: number;
}

interface SearchResult {
    id: string;
    filename: string;
    source_location?: string;
    totalHits: number;
    termMatches: TermMatch[];
    pdfFilename?: string;
    associate_name?: string;
    folderName?: string;
    displayName?: string;
}

interface ProcessingStatus {
    totalFiles: number;
    processedFiles: number;
    failedFiles: number;
    status: string;
    lastProposalIndexTime: string | null;
    lastCvIndexTime: string | null;
    errors?: Array<{ filename: string; error: string }>;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}



const HighlightedText = ({ content, searchTerms, maxChars = 300 }: {
    content: string | undefined;
    searchTerms: string[];
    maxChars?: number;
}) => {
    // Safety check for undefined content
    if (!content) return <span className="text-gray-500">No content available</span>;

    // If content doesn't already have highlights, add them
    let processedContent = content;
    const hasHighlights = content.includes('<b>') && content.includes('</b>');

    if (!hasHighlights) {
        // Add highlights manually
        searchTerms.forEach(term => {
            if (!term) return;
            const regex = new RegExp(`(${term.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')})`, 'gi');
            processedContent = processedContent.replace(regex, '<b>$1</b>');
        });
    }

    // If the content is already short enough, return it as is
    if (processedContent.length <= maxChars) {
        return (
            <span
                className="search-result-content"
                dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(processedContent
                        .replace(/<b>/g, '<span style="color: #1976d2; font-weight: bold;">')
                        .replace(/<\/b>/g, '</span>'))
                }}
            />
        );
    }

    // BRAND NEW APPROACH: Sentence-based truncation

    // First, find where the highlight is located (to center our context around it)
    const highlightPos = processedContent.indexOf('<b>');
    const centerPos = highlightPos !== -1 ? highlightPos : Math.floor(processedContent.length / 2);

    // Split text into sentences using a robust regex
    // This matches sentence endings with periods, question marks, exclamation marks
    // followed by a space or HTML tag
    const sentenceSplitRegex = /([.!?][\s\n<])/g;

    // Replace sentence endings with a special marker we can split on
    const markedText = processedContent.replace(sentenceSplitRegex, '$1[[SENTENCE_BREAK]]');

    // Split on our marker to get array of sentences (with punctuation preserved)
    let sentences = markedText.split('[[SENTENCE_BREAK]]');

    // Clean up empty entries and trim each sentence
    sentences = sentences.filter(s => s.trim().length > 0);

    // Find which sentence contains our highlight/center position
    let centerSentenceIndex = 0;
    let currentPos = 0;

    for (let i = 0; i < sentences.length; i++) {
        const sentenceLength = sentences[i].length;
        if (currentPos <= centerPos && (currentPos + sentenceLength) > centerPos) {
            centerSentenceIndex = i;
            break;
        }
        currentPos += sentenceLength;
    }

    // Choose sentences around our center sentence until we reach maxChars
    // Always include the center sentence
    const selectedSentences = [sentences[centerSentenceIndex]];
    let totalLength = sentences[centerSentenceIndex].length;

    // Add sentences before and after alternately until we reach maxChars
    let beforeIndex = centerSentenceIndex - 1;
    let afterIndex = centerSentenceIndex + 1;

    while (totalLength < maxChars) {
        // Try to add a sentence after (if available)
        if (afterIndex < sentences.length) {
            selectedSentences.push(sentences[afterIndex]);
            totalLength += sentences[afterIndex].length;
            afterIndex++;
        }

        // If we still have room, try to add a sentence before (if available)
        if (totalLength < maxChars && beforeIndex >= 0) {
            selectedSentences.unshift(sentences[beforeIndex]);
            totalLength += sentences[beforeIndex].length;
            beforeIndex--;
        }

        // If we can't add any more sentences in either direction, break
        if ((afterIndex >= sentences.length && beforeIndex < 0) || totalLength >= maxChars) {
            break;
        }
    }

    // Combine selected sentences into a single string
    let displayText = selectedSentences.join('');

    // Add ellipses if we're not showing the full text
    if (beforeIndex >= 0) {
        displayText = '... ' + displayText;
    }

    if (afterIndex < sentences.length) {
        displayText = displayText + ' ...';
    }

    // Ensure HTML tags are balanced
    const openBTags = (displayText.match(/<b>/g) || []).length;
    const closeBTags = (displayText.match(/<\/b>/g) || []).length;

    if (openBTags > closeBTags) {
        displayText += '</b>'.repeat(openBTags - closeBTags);
    } else if (closeBTags > openBTags) {
        displayText = '<b>'.repeat(closeBTags - openBTags) + displayText;
    }

    return (
        <span
            className="search-result-content"
            dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(displayText
                    .replace(/<b>/g, '<span style="color: #1976d2; font-weight: bold;">')
                    .replace(/<\/b>/g, '</span>'))
            }}
        />
    );
};

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`tabpanel-${index}`}
            aria-labelledby={`tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    <Typography>{children}</Typography>
                </Box>
            )}
        </div>
    );
}

// Define server URL - always use relative path for Azure
const SERVER_URL = '/api';
const MAX_RETRY_ATTEMPTS = 3;

// Drawer width
const drawerWidth = 240;

// Styled components for our layout
const AppContainer = styled('div')({
    display: 'flex',
    height: '100vh',
    overflow: 'hidden'
});

const SidebarContainer = styled('div')({
    width: drawerWidth,
    flexShrink: 0,
    borderRight: '1px solid rgba(0, 0, 0, 0.12)',
    overflowY: 'auto',
    background: '#fff',
    paddingTop: '64px', // Make room for AppBar
    height: '100vh',
    position: 'fixed',
    left: 0,
    top: 0,
    zIndex: 100
});

const MainContainer = styled('div')({
    flexGrow: 1,
    padding: theme.spacing(3),
    marginLeft: drawerWidth,
    paddingTop: '64px', // Make room for AppBar
    overflow: 'auto',
    height: '100vh'
});

// Define a modern dark theme
const darkTheme = createTheme({
    palette: {
        mode: 'dark',
        primary: { main: '#22B8A7' },
        background: { default: '#0E0F12', paper: '#14161A' },
        text: { primary: '#E8EAED', secondary: '#B9C0C8' },
        divider: 'rgba(255,255,255,0.12)'
    },
    typography: {
        fontFamily: 'Inter, "Helvetica Neue", Arial, sans-serif',
        h6: { fontWeight: 600 },
        body1: { fontSize: '1rem', lineHeight: 1.5 },
        body2: { fontSize: '0.9375rem', lineHeight: 1.45 },
    },
    components: {
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: '#14161A',
                    boxShadow: 'none',
                    borderBottom: '1px solid rgba(255,255,255,0.12)'
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    backgroundColor: '#0E0F12',
                    color: '#E8EAED',
                    borderRight: '1px solid rgba(255,255,255,0.12)'
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundColor: '#14161A',
                    borderRadius: 12,
                    backgroundImage: 'none'
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: { borderRadius: 12, textTransform: 'none', fontWeight: 600 },
                containedPrimary: {
                    backgroundColor: '#22B8A7',
                    color: '#0B0C10',
                    boxShadow: '0 8px 24px -4px rgba(0,0,0,0.35), 0 1px 0 rgba(255,255,255,0.02)',
                    '&:hover': { backgroundColor: '#1FA594' },
                },
                outlinedPrimary: {
                    borderColor: 'rgba(255,255,255,0.12)',
                    color: '#E8EAED',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 12,
                        backgroundColor: '#1B1E23',
                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.12)' },
                        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.24)' },
                        '&.Mui-focused fieldset': { borderColor: '#22B8A7' },
                    },
                    '& label.Mui-focused': { color: '#B9C0C8' },
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                select: { backgroundColor: '#1B1E23', borderRadius: 12 },
                outlined: { backgroundColor: '#1B1E23', borderRadius: 12 },
                icon: { color: 'rgba(255,255,255,0.54)' },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: { borderRadius: 10 },
                outlined: { borderColor: 'rgba(255,255,255,0.12)' },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    color: '#E8EAED',
                },
            },
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    color: '#22B8A7',
                    '&:hover': { color: '#1FA594' },
                },
            },
        },
        MuiLinearProgress: {
            styleOverrides: {
                colorPrimary: {
                    backgroundColor: 'rgba(34, 184, 167, 0.24)',
                },
                barColorPrimary: {
                    backgroundColor: '#22B8A7',
                },
            },
        },
        MuiCircularProgress: {
            styleOverrides: {
                colorPrimary: {
                    color: '#22B8A7',
                },
            },
        },
    },
});

// Main styled component with improved scrolling
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{ open?: boolean }>(
    ({ theme, open }) => ({
        flexGrow: 1,
        padding: theme.spacing(3),
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        marginLeft: 0,
        ...(open && {
            transition: theme.transitions.create('margin', {
                easing: theme.transitions.easing.easeOut,
                duration: theme.transitions.duration.enteringScreen,
            }),
            marginLeft: `${drawerWidth}px`,
        }),
        bgcolor: '#121416', // Darker background for content
        borderRadius: '4px',
        height: 'calc(100vh - 64px)', // Full height minus AppBar
        overflowY: 'auto', // Enable vertical scrolling
        display: 'flex',
        flexDirection: 'column'
    }),
);

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
    justifyContent: 'flex-end',
}));

// Helper function to get status display text/color (Restored)
const getStatusDisplay = (status: string | undefined | null) => { // Added undefined/null check
    switch (status?.toLowerCase()) {
        case 'idle': return { text: 'Idle', color: 'success' };
        case 'indexing': return { text: 'Indexing...', color: 'warning' };
        case 'error': return { text: 'Error', color: 'error' };
        default: return { text: status || 'Unknown', color: 'info' };
    }
};

// Create a new CVSearchResult component for better CV search result display
const CVSearchResult = ({
    result,
    onNotification,
    searchMode
}: {
    result: any;
    onNotification?: (message: string) => void;
    searchMode?: 'cv' | 'proposal';
}) => {
    const [expanded, setExpanded] = useState(false);
    const [activeTermIndex, setActiveTermIndex] = useState<number>(0);
    const navigate = useNavigate();

    // Determine if this is a proposal or CV result based on searchMode prop
    const isProposal = searchMode === 'proposal';
    console.log(`Result type: ${isProposal ? 'PROPOSAL' : 'CV'}, ID: ${result.id || 'unknown'}`);

    // Get total hits
    const totalHits = result.totalHits || 0;

    // Get document name - different logic for proposals vs CVs
    let documentName = '';

    if (isProposal) {
        // For proposals: use displayName or filename, remove .pdf extension
        documentName = result.displayName || result.filename?.replace(/\.pdf$/i, '') || 'Unknown Document';
    } else {
        // For CVs: use the existing logic but extract only filename without folder prefix
        // First priority: Use the pdfFilename field from server response
        if (result.pdfFilename) {
            // Extract just the filename from the path
            const pathParts = result.pdfFilename.split(/[\/\\]+/);
            const filename = pathParts[pathParts.length - 1];
            documentName = filename.replace(/\.pdf$/i, '');
        }
        // Second priority: Try associate_name
        else if (result.associate_name) {
            // Extract just the filename from the path
            const pathParts = result.associate_name.split(/[\/\\]+/);
            const filename = pathParts[pathParts.length - 1];
            documentName = filename.replace(/\.pdf$/i, '');
        }
        // Third priority: Extract from source_location 
        else if (result.source_location) {
            const pathParts = result.source_location.split(/[\/\\]+/);
            const lastPart = pathParts[pathParts.length - 1];

            if (lastPart && lastPart.toLowerCase().endsWith('.pdf')) {
                documentName = lastPart.replace(/\.pdf$/i, '');
            }
        }
        // Fall back to ID if we still couldn't find a filename
        if (!documentName) {
            // If ID contains a path, extract just the filename
            if (result.id && typeof result.id === 'string' && (result.id.includes('/') || result.id.includes('\\'))) {
                const pathParts = result.id.split(/[\/\\]+/);
                const filename = pathParts[pathParts.length - 1];
                documentName = filename.replace(/\.pdf$/i, '') || `Document ${result.id}`;
            } else {
                documentName = result.id ? `Document ${result.id}` : 'Unknown Document';
            }
        }
    }

    // Get folder name for subtitle (only for CVs)
    let folderName = '';
    if (!isProposal && result) {
        console.log('[CVSearchResult] Extracting folder name for:', {
            id: result.id || 'N/A',
            filename: result.filename || 'N/A',
            pdfFilename: result.pdfFilename || 'N/A',
        });

        // Add detailed debugging for all properties
        console.log('[CVSearchResult] Result properties:', {
            folderName: result.folderName,
            source_location: result.source_location,
            associate_name: result.associate_name,
            termMatches: result.termMatches ? `${result.termMatches.length} matches` : 'none',
            totalHits: result.totalHits
        });

        if (result.folderName && result.folderName !== 'Unknown Folder') {
            // First priority: Use folderName if directly available and not the unknown placeholder
            folderName = result.folderName;
            console.log('[CVSearchResult] Using provided folderName:', folderName);
        } else if (result.source_location) {
            // Second priority: Extract from source_location
            const pathParts = result.source_location.split(/[\/\\]+/);
            console.log('[CVSearchResult] Path parts from source_location:', pathParts);

            if (pathParts.length > 2) {
                // Get the second-to-last part (parent directory)
                folderName = pathParts[pathParts.length - 2];
                console.log('[CVSearchResult] Extracted folderName from source_location:', folderName);
            } else {
                console.log('[CVSearchResult] Not enough path parts to extract folder name');
            }
        } else if (result.id && typeof result.id === 'string' && result.id.includes('\\')) {
            // Third priority: Extract from id if it has the format "FolderName\Filename.pdf"
            const pathParts = result.id.split('\\');
            console.log('[CVSearchResult] Path parts from id:', pathParts);

            if (pathParts.length >= 2) {
                // First part should be the folder name
                folderName = pathParts[0];
                console.log('[CVSearchResult] Extracted folderName from id:', folderName);
            }
        } else {
            console.log('[CVSearchResult] No folderName, source_location, or id with folder format available');
        }

        // Only use fallback if we couldn't extract a folder name
        if (!folderName) {
            folderName = 'Unknown Folder';
            console.log('[CVSearchResult] Using fallback folderName:', folderName);
        }
    }

    // Normalize term matches structure for consistent rendering
    let normalizedTermMatches: any[] = [];

    if (isProposal) {
        // For proposals: use termMatches directly (same structure as CVs)
        console.log(`[CVSearchResult] PROPOSAL termMatches debug for ${result.displayName || result.filename}:`, {
            hasTermMatches: !!result.termMatches,
            termMatchesType: Array.isArray(result.termMatches) ? 'array' : typeof result.termMatches,
            termMatchesLength: result.termMatches?.length || 0,
            termMatchesContent: result.termMatches
        });

        if (result.termMatches && Array.isArray(result.termMatches)) {
            normalizedTermMatches = result.termMatches;
            console.log(`[CVSearchResult] Using ${normalizedTermMatches.length} termMatches for proposal`);
        } else {
            normalizedTermMatches = [];
            console.log(`[CVSearchResult] No valid termMatches found for proposal, using empty array`);
        }
    } else {
        // Handle CV search results
        if (!result.termMatches) {
            // If no termMatches at all, use empty array
            normalizedTermMatches = [];
        } else if (Array.isArray(result.termMatches)) {
            // If already an array, use directly
            normalizedTermMatches = result.termMatches;
        } else {
            // It's an object of terms, convert to array format
            try {
                console.log('Converting termMatches object to array:', result.termMatches);
                normalizedTermMatches = Object.entries(result.termMatches).map(([term, details]: [string, any]) => ({
                    term: term,
                    hitCount: details.hitCount || 0,
                    matches: details.matches || [],
                    matchType: details.matchType
                }));
            } catch (error) {
                console.error('Error normalizing termMatches:', error);
                normalizedTermMatches = []; // Empty array in case of error
            }
        }
    }

    console.log(`[CVSearchResult] Final normalizedTermMatches for ${isProposal ? 'PROPOSAL' : 'CV'}:`, {
        length: normalizedTermMatches.length,
        terms: normalizedTermMatches.map(tm => tm.term)
    });

    // Function to handle viewing the document in a new tab
    const handleViewDocument = () => {
        console.log('View document clicked:', result);

        // Extract document path with robust fallbacks - use same logic as chat function
        let documentPath;

        if (isProposal) {
            // For proposals
            if (result.filename) {
                documentPath = result.filename;
                console.log('Using filename:', documentPath);
            } else if (result.displayName) {
                documentPath = result.displayName;
                if (!documentPath.toLowerCase().endsWith('.pdf')) {
                    documentPath += '.pdf';
                }
                console.log('Using displayName:', documentPath);
            }
        } else {
            // For CVs - use the same robust fallback logic as the chat function
            documentPath = result.source_location || result.associate_name || result.pdfFilename || result.filename || result.id || '';
            console.log('Using CV document path with fallbacks:', documentPath);

            // Ensure .pdf if it's a simple name without extension and no path separators
            if (documentPath && typeof documentPath === 'string' && !documentPath.toLowerCase().endsWith('.pdf') && !documentPath.includes('\\') && !documentPath.includes('/')) {
                documentPath += ".pdf";
            }
        }

        if (!documentPath) {
            console.error('No document path available', result);
            if (onNotification) onNotification('Unable to determine document path');
            return;
        }

        try {
            // Clean the document path to remove any unwanted characters
            let cleanPath = documentPath;
            if (cleanPath.startsWith('@')) {
                cleanPath = cleanPath.substring(1);
                console.log('🐛 Debug - Removed @ prefix, new path:', cleanPath);
            }

            // Fix folder name mapping for Azure blob storage
            // Database has "International" but blob storage has "1. International"
            if (cleanPath.startsWith('International/')) {
                cleanPath = cleanPath.replace('International/', '1. International/');
                console.log('📁 Fixed International folder path:', cleanPath);
            }

            console.log('📄 Attempting to open document:', cleanPath);

            // Use the DocumentViewer route to maintain proper favicon
            const encodedPath = encodeURIComponent(cleanPath);
            const documentType = isProposal ? 'proposal' : 'cv';
            const viewerUrl = `/document/${documentType}/${encodedPath}`;
            console.log('📄 Opening document via DocumentViewer:', viewerUrl);

            // Open the document through our DocumentViewer component
            window.open(`#${viewerUrl}`, '_blank');

        } catch (error) {
            console.error('Error opening document:', error);
            if (onNotification) onNotification(`Unable to access document "${result.displayName || result.filename}". The document viewing service may be temporarily unavailable.`);
        }
    };

    // Function to handle chatting with the document
    const handleChatWithDocument = () => {
        console.log('=== CHAT WITH DOCUMENT DEBUG ===');
        console.log('Chat with document clicked:', result);
        console.log('isProposal:', isProposal);
        console.log('Available result fields:', Object.keys(result));

        let docIdentifier = '';
        const docType = isProposal ? 'proposal' : 'cv';

        if (isProposal) {
            console.log('=== PROPOSAL CHAT DEBUG ===');
            console.log('result.filename:', result.filename);
            console.log('result.displayName:', result.displayName);

            // For proposals, filename is a good identifier if available
            docIdentifier = result.filename || result.displayName || '';
            console.log('Initial docIdentifier:', docIdentifier);

            // Ensure .pdf if it's a display name without extension
            if (result.displayName && docIdentifier && typeof docIdentifier === 'string' && !docIdentifier.toLowerCase().endsWith('.pdf')) {
                docIdentifier += '.pdf';
                console.log('Added .pdf extension, new docIdentifier:', docIdentifier);
            }
        } else { // CV
            console.log('=== CV CHAT DEBUG ===');
            console.log('result.source_location:', result.source_location);
            console.log('result.associate_name:', result.associate_name);
            console.log('result.pdfFilename:', result.pdfFilename);
            console.log('result.filename:', result.filename);
            console.log('result.id:', result.id);

            // For CVs, use the new metadata fields with fallbacks
            docIdentifier = result.source_location || result.associate_name || result.pdfFilename || result.filename || result.id || '';
            console.log('Initial CV docIdentifier:', docIdentifier);

            // Ensure .pdf if it's a simple name without extension and no path separators
            if (docIdentifier && typeof docIdentifier === 'string' && !docIdentifier.toLowerCase().endsWith('.pdf') && !docIdentifier.includes('\\') && !docIdentifier.includes('/')) {
                docIdentifier += ".pdf";
                console.log('Added .pdf extension to CV, new docIdentifier:', docIdentifier);
            }
        }

        console.log('Final docIdentifier:', docIdentifier);
        console.log('Final docType:', docType);

        if (!docIdentifier) {
            console.error("Could not determine document identifier for chat:", result);
            if (onNotification) onNotification("Error: Could not identify the document for chat.");
            return;
        }

        // Document identifiers can contain characters that need encoding for URLs
        const encodedIdentifier = encodeURIComponent(docIdentifier);
        console.log('Encoded identifier:', encodedIdentifier);
        console.log('Final navigation URL:', `/chat/${docType}/${encodedIdentifier}`);

        navigate(`/chat/${docType}/${encodedIdentifier}`);
    };

    return (
        <Paper elevation={0} className="search-result" sx={{
            mb: 2,
            p: 2.5,
            borderRadius: 2,
            backgroundColor: 'background.paper',
            border: '1px solid rgba(255,255,255,0.12)',
            boxShadow: '0 8px 24px -4px rgba(0,0,0,0.35), 0 1px 0 rgba(255,255,255,0.02)',
            transition: 'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',
            '&:hover': {
                transform: 'translateY(-1px)',
                borderColor: 'rgba(255,255,255,0.2)'
            }
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Box sx={{
                    flexGrow: 1,
                    mr: 2,
                    overflow: 'hidden'
                }}>
                    <Typography variant="h6" component="h3" sx={{
                        fontWeight: 'bold',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        hyphens: 'auto',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.3
                    }}>
                        {documentName}
                    </Typography>
                    {/* Only show folder name for CVs */}
                    {!isProposal && folderName && (
                        <Typography variant="subtitle2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            {folderName}
                        </Typography>
                    )}
                </Box>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0 // Prevent this box from shrinking
                }}>
                    <Chip
                        label={`${totalHits} hits`}
                        size="small"
                        variant="outlined"
                        sx={{
                            mr: 1,
                            borderColor: 'rgba(255,255,255,0.12)',
                            color: 'text.secondary',
                            backgroundColor: '#1E232A',
                            '& .MuiChip-label': { fontWeight: 600 }
                        }}
                    />

                    {/* Chat Button - Temporarily disabled, may re-enable later
                    <Tooltip title={`Chat about this ${isProposal ? 'Proposal' : 'CV'}`}>
                        <IconButton
                            onClick={handleChatWithDocument}
                            size="small"
                            sx={{
                                mr: 0.5,
                                color: 'primary.main',
                                '&:hover': {
                                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                }
                            }}
                        >
                            <ChatBubbleOutlineIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    */}

                    {/* View Document Button */}
                    <Tooltip title={`View ${isProposal ? 'Proposal' : 'CV'} in new tab`}>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleViewDocument}
                            sx={{
                                mr: 1,
                                minWidth: 120,
                                px: 1.5,
                                py: 0.75,
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '0.8125rem',
                                display: { xs: 'none', sm: 'flex' },
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'background-color 0.18s ease, box-shadow 0.18s ease'
                            }}
                            startIcon={isProposal ?
                                <DescriptionIcon fontSize="small" /> :
                                <PersonIcon fontSize="small" />
                            }
                        >
                            View {isProposal ? 'Proposal' : 'CV'}
                        </Button>
                    </Tooltip>

                    <IconButton
                        onClick={() => setExpanded(!expanded)}
                        aria-expanded={expanded}
                        aria-label="show more"
                    >
                        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                {normalizedTermMatches.map((tm: any, index: number) => (
                    <Chip
                        key={index}
                        label={`${tm.term}: ${tm.hitCount}`}
                        variant="outlined"
                        size="small"
                        sx={{
                            bgcolor: 'rgba(25, 118, 210, 0.1)',
                            borderColor: 'primary.main'
                        }}
                        onClick={() => {
                            if (expanded) {
                                setActiveTermIndex(index);
                            } else {
                                setExpanded(true);
                                setActiveTermIndex(index);
                            }
                        }}
                    />
                ))}
            </Box>

            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <Divider sx={{ my: 1.5 }} />

                {/* Term-specific tabs */}
                {normalizedTermMatches.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs
                                value={activeTermIndex}
                                onChange={(e, newValue) => setActiveTermIndex(newValue)}
                                variant="scrollable"
                                scrollButtons="auto"
                            >
                                {normalizedTermMatches.map((tm: any, index: number) => (
                                    <Tab
                                        key={index}
                                        label={`${tm.term} (${tm.hitCount})`}
                                        id={`term-tab-${index}`}
                                        aria-controls={`term-tabpanel-${index}`}
                                    />
                                ))}
                            </Tabs>
                        </Box>

                        {normalizedTermMatches.map((tm: any, index: number) => (
                            <div
                                key={index}
                                role="tabpanel"
                                hidden={activeTermIndex !== index}
                                id={`term-tabpanel-${index}`}
                                aria-labelledby={`term-tab-${index}`}
                            >
                                {activeTermIndex === index && (
                                    <Box sx={{ p: 2 }}>
                                        {/* Show context snippets for this term */}
                                        {tm.matches && tm.matches.length > 0 ? (
                                            tm.matches.slice(0, 10).map((match: any, mIndex: number) => (
                                                <Paper key={mIndex} variant="outlined" sx={{ p: 1.5, mb: 1, bgcolor: 'rgba(0,0,0,0.02)' }}>
                                                    <HighlightedText
                                                        content={match.context}
                                                        searchTerms={[tm.term]}
                                                        maxChars={500}
                                                    />
                                                </Paper>
                                            ))
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                No detailed context available for this term.
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            </div>
                        ))}
                    </Box>
                )}

                {/* Sections display (if available) */}
                {result.sections?.map((section: any, sIndex: number) => (
                    <Box key={sIndex} sx={{ mt: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                            {section.type}
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'rgba(0,0,0,0.02)' }}>
                            <HighlightedText content={section.content} searchTerms={normalizedTermMatches.map((tm: any) => tm.term)} maxChars={350} />
                        </Paper>
                    </Box>
                ))}
            </Collapse>
        </Paper>
    );
};



// Add this function near the top of the file, after imports but before component definitions
// This will be accessible via the global window object for testing
declare global {
    interface Window {
        testXlsxExport: () => void;
    }
}

// Inside App component initialization, add this function:
// Simple test function for Excel that can be called from the console
const testXlsxExport = async () => {
    try {
        console.log('Testing Excel export...');

        // Create workbook and worksheet
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Test Sheet');

        // Add data
        worksheet.addRow(['Name', 'Age', 'City']);
        worksheet.addRow(['John Doe', 30, 'New York']);
        worksheet.addRow(['Jane Smith', 25, 'London']);
        worksheet.addRow(['Bob Johnson', 40, 'Paris']);

        console.log('Worksheet created');

        // Generate binary data
        console.log('Writing to array buffer...');
        const buffer = await workbook.xlsx.writeBuffer();
        console.log('Array buffer created, length:', buffer.byteLength);

        // Create blob and download
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = URL.createObjectURL(blob);
        console.log('Blob URL created:', url);

        const a = document.createElement('a');
        a.href = url;
        a.download = 'test_export.xlsx';
        console.log('Download element created');

        document.body.appendChild(a);
        console.log('Element added to document');

        a.click();
        console.log('Download triggered');

        setTimeout(() => {
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            console.log('Cleanup completed');
        }, 100);

        console.log('Test export completed successfully');
        alert('Test export completed - check downloads folder');
    } catch (error: any) {
        console.error('Test export failed:', error);
        alert(`Test export failed: ${error.message}`);
    }
};

// Make the test function available globally
window.testXlsxExport = testXlsxExport;

// Convert the main App content to a SearchPage component 
const SearchPageComponent = ({
    searchQuery, setSearchQuery,
    searchResults, setSearchResults,
    isLoading, setIsLoading,
    isReindexing, reindexOperation,
    searchMode,
    progress, setProgress,
    snackbarMessage,
    error, setError,
    handleSearch,
    handleSetNotification,
    displayedResults,
    uniqueFolders,
    folderFilter, setFolderFilter,
    exportToExcel,
    proposalOutcomeFilter, setProposalOutcomeFilter,
    useMixedHits, handleMixedHitsChange
}: any) => {
    return (
        <Container maxWidth="xl" sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
        }}>
            {/* Search inputs */}
            <Box sx={{ flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, position: 'relative' }}>
                    <Tooltip
                        title={
                            <Box sx={{ p: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    Boolean Search Help
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    Use search operators for precise results:
                                </Typography>
                                <Box component="ul" sx={{ pl: 2, mb: 1, '& li': { mb: 0.5 } }}>
                                    <li><strong>AND</strong> - Find documents with all terms: <em>"climate change" AND Spanish</em></li>
                                    <li><strong>OR</strong> - Find documents with any terms: <em>Spanish OR French OR Arabic</em></li>
                                    <li><strong>Quotes</strong> - Exact phrases: <em>"project management"</em></li>
                                    <li><strong>Parentheses</strong> - Group terms: <em>(Spanish OR French) AND "climate change"</em></li>
                                </Box>
                                <Typography variant="body2" sx={{ fontStyle: 'italic', fontSize: '0.75rem' }}>
                                    Examples: education AND (Kenya OR Tanzania) • "water sanitation" OR "clean water" • French AND "public health"
                                </Typography>
                            </Box>
                        }
                        arrow
                        placement="bottom-start"
                        componentsProps={{
                            tooltip: {
                                sx: {
                                    maxWidth: 400,
                                    bgcolor: 'background.paper',
                                    color: 'text.primary',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    '& .MuiTooltip-arrow': {
                                        color: 'background.paper',
                                        '&::before': {
                                            border: '1px solid',
                                            borderColor: 'divider',
                                        }
                                    }
                                }
                            }
                        }}
                    >
                        <IconButton
                            aria-label="Boolean search help"
                            size="small"
                            sx={{
                                mr: 1,
                                color: 'rgba(255, 255, 255, 0.7)',
                                '&:hover': {
                                    color: 'white',
                                    backgroundColor: 'rgba(255, 255, 255, 0.08)'
                                }
                            }}
                        >
                            <HelpOutlineIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <TextField
                        fullWidth
                        variant="outlined"
                        label={`Search ${searchMode === 'proposal' ? 'Proposals' : 'CVs'}`}
                        value={searchQuery}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                        onKeyPress={(e: React.KeyboardEvent) => { if (e.key === 'Enter') handleSearch(); }}
                        sx={{ mr: 1 }}
                        disabled={isLoading}
                        InputProps={{
                            endAdornment: searchQuery ? (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="clear search"
                                        onClick={() => {
                                            setSearchQuery('');
                                            setSearchResults([]);
                                        }}
                                        edge="end"
                                        size="small"
                                        sx={{
                                            color: 'rgba(255, 255, 255, 0.7)',
                                            '&:hover': {
                                                color: 'white',
                                                backgroundColor: 'rgba(255, 255, 255, 0.08)'
                                            }
                                        }}
                                    >
                                        <Tooltip title="Clear search">
                                            <CloseIcon fontSize="small" />
                                        </Tooltip>
                                    </IconButton>
                                </InputAdornment>
                            ) : null
                        }}
                    />
                    <Button
                        variant="contained"
                        onClick={handleSearch}
                        disabled={isLoading}
                        startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                        sx={{ minWidth: 120, height: '56px' }} // Match text field height
                    >
                        {isLoading ? 'Searching...' : 'Search'}
                    </Button>
                </Box>

                {/* Use Mixed Hits Toggle */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Tooltip title="When enabled, search results prioritize documents with balanced matches across all keywords. For example, a document with 6 hits for 'climate' and 6 hits for 'water' will rank higher than one with 100 hits for 'climate' but only 1 hit for 'water'. This requires a NEW SEARCH when toggled." arrow>
                        <FormControlLabel
                            control={<Switch checked={useMixedHits} onChange={handleMixedHitsChange} size="small" />}
                            label="Mixed Hits (balanced term distribution - press Search to apply)"
                            labelPlacement="start"
                            sx={{ mr: 1 }}
                        />
                    </Tooltip>
                </Box>

                {/* Add Proposal Outcome Filter - only shown for proposal searches */}
                {searchMode === 'proposal' && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                        <FormControl component="fieldset">
                            <Tooltip title="Filter proposals by outcome status (Successful/Unsuccessful)">
                                <FormLabel component="legend" sx={{ fontSize: '0.875rem', mr: 2 }}>Proposal Outcome:</FormLabel>
                            </Tooltip>
                            <RadioGroup
                                row
                                aria-label="proposal-outcome-filter"
                                name="proposal-outcome-filter"
                                value={proposalOutcomeFilter}
                                onChange={(e) => setProposalOutcomeFilter(e.target.value as 'all' | 'successful' | 'unsuccessful')}
                            >
                                <FormControlLabel value="all" control={<Radio size="small" />} label="All" />
                                <FormControlLabel value="successful" control={<Radio size="small" />} label="Successful" />
                                <FormControlLabel value="unsuccessful" control={<Radio size="small" />} label="Unsuccessful" />
                            </RadioGroup>
                        </FormControl>
                    </Box>
                )}

                {isLoading && (
                    <Box sx={{ width: '100%', my: 2 }}>
                        <LinearProgress variant="determinate" value={progress} />
                        <Typography variant="caption" display="block" gutterBottom align="center">
                            {progress < 100 ? `Processing... ${Math.round(progress)}%` : 'Finalizing...'}
                        </Typography>
                    </Box>
                )}

                {snackbarMessage && (
                    <Alert severity="info" sx={{ mb: 2 }}>{snackbarMessage}</Alert>
                )}
            </Box>

            {/* Results Area - With Scrolling */}
            <Box sx={{
                flexGrow: 1,
                overflow: 'auto',
                mt: 2
            }}>
                {/* Enhanced Loading Indicator with Cold Start Detection */}
                <LoadingIndicator
                    isVisible={isLoading || isReindexing}
                    message={
                        isReindexing
                            ? `${reindexOperation === 'proposal' ? 'Vectorizing Proposals' : 'Reindexing CVs'}...`
                            : `Searching ${searchMode === 'proposal' ? 'Proposals' : 'CVs'}...`
                    }
                    isReindexing={isReindexing}
                    operationType={isReindexing ? reindexOperation : searchMode}
                />

                {!isLoading && (
                    <>
                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        {displayedResults.length === 0 && !error ? (
                            <Box sx={{ textAlign: 'center', mt: 4 }}>
                                <SearchOffIcon sx={{ fontSize: 60, color: 'text.secondary' }} />
                                <Typography variant="h6" color="text.secondary">
                                    No results found
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Try adjusting your search terms or search type
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                {/* Box with search result count and actions */}
                                <Box sx={{
                                    mb: 4,
                                    pb: 2,
                                    display: 'flex',
                                    justifyContent: 'flex-start', // Changed from space-between to flex-start
                                    alignItems: 'flex-start',
                                    flexWrap: 'wrap',
                                    gap: 4, // Increased gap
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 1001,
                                    backgroundColor: darkTheme.palette.background.default,
                                    borderBottom: '1px solid rgba(255,255,255,0.1)'
                                }}>
                                    <Typography variant="h6" sx={{ alignSelf: 'center', mr: 4 }}>
                                        Found {displayedResults.length} results
                                    </Typography>

                                    {/* Only show folder filter and export button for CV search results */}
                                    {searchMode === 'cv' && (
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            flexWrap: 'wrap',
                                            gap: 3,
                                            position: 'relative',
                                            mb: 1,
                                            pt: 1,
                                            pb: 1,
                                            marginLeft: 0 // Ensure no left margin
                                        }}>
                                            {uniqueFolders.length > 1 && (
                                                <FormControl size="small" sx={{
                                                    width: 300,
                                                    zIndex: 1050,
                                                    position: 'relative',
                                                    marginLeft: 0,
                                                    '& .MuiOutlinedInput-root': {
                                                        color: 'text.primary',
                                                        borderRadius: 2,
                                                        backgroundColor: '#1B1E23',
                                                        '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                                                        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.24)' },
                                                        '&.Mui-focused fieldset': { borderColor: 'primary.main' }
                                                    },
                                                    '& .MuiSelect-select': { paddingRight: '32px' }
                                                }}>
                                                    <InputLabel id="folder-filter-label" sx={{ color: 'text.secondary' }}>Filter by Country</InputLabel>
                                                    <Select
                                                        labelId="folder-filter-label"
                                                        id="folder-filter"
                                                        value={folderFilter}
                                                        label="Filter by Country"
                                                        onChange={(e) => setFolderFilter(e.target.value)}
                                                        MenuProps={{
                                                            PaperProps: {
                                                                style: {
                                                                    maxHeight: 300,
                                                                    zIndex: 9999
                                                                }
                                                            },
                                                            anchorOrigin: {
                                                                vertical: 'bottom',
                                                                horizontal: 'left',
                                                            },
                                                            transformOrigin: {
                                                                vertical: 'top',
                                                                horizontal: 'left',
                                                            },
                                                            disablePortal: false,
                                                            marginThreshold: 0,
                                                            sx: {
                                                                mt: 1,
                                                                '& .MuiMenu-list': {
                                                                    padding: 1
                                                                }
                                                            }
                                                        }}
                                                        sx={{
                                                            color: 'text.primary',
                                                            '& .MuiSvgIcon-root': { color: 'text.secondary' }
                                                        }}
                                                    >
                                                        {uniqueFolders.map((folder: string) => (
                                                            <MenuItem key={folder} value={folder}>
                                                                {folder === 'all' ? 'All Countries' : folder}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            )}

                                            <Button
                                                variant="outlined"
                                                startIcon={<FileDownloadIcon />}
                                                onClick={exportToExcel}
                                                size="small"
                                                sx={{
                                                    minWidth: 145,
                                                    whiteSpace: 'nowrap'
                                                    // Removed ml: 2 to avoid pushing it right
                                                }}
                                            >
                                                Export to Excel
                                            </Button>
                                        </Box>
                                    )}
                                </Box>

                                {/* Add more space above the search results */}
                                <Box sx={{
                                    overflowY: 'auto',
                                    mt: 3  // Add margin top to create space
                                }}>
                                    {displayedResults.map((result: any, index: number) => (
                                        <CVSearchResult
                                            key={index}
                                            result={result}
                                            onNotification={handleSetNotification}
                                            searchMode={searchMode}
                                        />
                                    ))}
                                </Box>
                            </>
                        )}
                    </>
                )}
            </Box>
        </Container>
    );
};

// Add the MainLayout component to handle app layout and routing
const MainLayout = ({
    drawerOpen, setDrawerOpen,
    statusInfo, isLoading, isReindexing,
    handleReindexCVs, handleReindexProposals, handleReindexAll,
    setInfoDialogOpen, infoDialogOpen,
    searchMode, setSearchMode, setSearchQuery, setSearchResults, formatDate,
    snackbarOpen, snackbarMessage, handleCloseNotification, setSnackbarMessage, setSnackbarOpen,
    dbConnectionStatus,
    userEmail,
    reindexJob,
    travelResourcesAnchorEl,
    setTravelResourcesAnchorEl
}: any) => {
    const navigate = useNavigate(); // Add useNavigate hook
    
    // State for collapsible sections
    const [travelSectionOpen, setTravelSectionOpen] = useState(false);
    const [bidSupportSectionOpen, setBidSupportSectionOpen] = useState(false);
    const [formsSectionOpen, setFormsSectionOpen] = useState(false);
    const [adminSectionOpen, setAdminSectionOpen] = useState(false);
    const [projectsSectionOpen, setProjectsSectionOpen] = useState(false);

    // Function to get user email for admin checks
    const getUserEmail = (): string | null => {
        return userEmail;
    };

    return (
        <ThemeProvider theme={darkTheme}>
            <CssBaseline />
            {/* AppBar */}
            <AppBar
                position="fixed"
                elevation={0}
                sx={{ zIndex: (theme: Theme) => theme.zIndex.drawer + 1 }}
            >
                <Toolbar disableGutters sx={{ padding: 0, height: '54px', minHeight: '54px !important' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexGrow: 1, pl: 2, pr: 2 }}>
                        <Typography variant="h6" noWrap component="div" sx={{ color: 'rgb(0,165,163)', /* ... */ }}>
                            DocHub
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {/* Database Connection Status Indicator */}
                            {dbConnectionStatus && (
                                <Chip
                                    icon={dbConnectionStatus.connected ? <CheckCircleIcon fontSize="small" /> : <ErrorIcon fontSize="small" />}
                                    label={dbConnectionStatus.connected ? 'Database Connected' : 'Database Disconnected'}
                                    color={dbConnectionStatus.connected ? undefined : 'error'}
                                    variant="outlined"
                                    size="small"
                                    sx={{ 
                                        mr: 2,
                                        ...(dbConnectionStatus.connected && {
                                            borderColor: 'rgb(0,165,163)',
                                            color: 'rgb(0,165,163)',
                                            '& .MuiChip-icon': {
                                                color: 'rgb(0,165,163)'
                                            }
                                        })
                                    }}
                                    onClick={() => {
                                        if (setSnackbarMessage && setSnackbarOpen) {
                                            setSnackbarMessage(dbConnectionStatus.message);
                                            setSnackbarOpen(true);
                                        }
                                    }}
                                />
                            )}
                            <IconButton color="inherit" aria-label="toggle drawer" onClick={() => setDrawerOpen(!drawerOpen)} edge="start" sx={{ mr: 1, color: 'rgba(255,255,255,0.7)' }}>
                                {drawerOpen ? <MenuOpenIcon /> : <MenuIcon />}
                            </IconButton>
                            <Tooltip title="System Status & Actions">
                                <IconButton color="inherit" onClick={() => setInfoDialogOpen(true)} size="small" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                    <InfoIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                </Toolbar>
            </AppBar>

            {/* Main layout container */}
            <div style={{
                paddingTop: '54px',
                display: 'flex',
                overflow: 'hidden',
                position: 'relative',
                width: '100%',
                height: '100vh'
            }}>
                {/* Sidebar */}
                <Box
                    component="div"
                    sx={{
                        width: `${drawerWidth}px`,
                        minWidth: `${drawerWidth}px`,
                        bgcolor: darkTheme.palette.background.paper,
                        borderRight: (theme: Theme) => `1px solid ${theme.palette.divider}`,
                        position: 'fixed',
                        top: '54px',
                        left: 0,
                        bottom: 0,
                        overflowY: 'auto',
                        pt: 1.5,
                        pb: 2,
                        px: 1,
                        display: drawerOpen ? 'block' : 'none'
                    }}
                >
                    {/* Search / Analysis Section - Super Admin only */}
                    <SuperAdminOnlySection userEmail={getUserEmail()}>
                    <Box sx={{ mb: 2 }}>
                        {/* Use List with dense for navigation items */}
                        <List component="nav" dense sx={{ px: 1 }}>
                            {/* CV Search Button */}
                            <ListItemButton
                                selected={searchMode === 'cv'}
                                onClick={() => {
                                    setSearchMode('cv');
                                    setSearchQuery('');
                                    setSearchResults([]);
                                    // Navigate to main search page
                                    navigate('/');
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                    <PersonIcon fontSize="small" color={searchMode === 'cv' ? 'primary' : 'inherit'} />
                                </ListItemIcon>
                                <ListItemText primary="CV Search" />
                            </ListItemButton>

                            {/* CV Match Button */}
                            <ListItemButton
                                selected={searchMode === 'cv-match'}
                                onClick={() => {
                                    setSearchMode('cv-match');
                                    setSearchQuery('');
                                    setSearchResults([]);
                                    navigate('/cv-match');
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                    <PersonIcon fontSize="small" color={searchMode === 'cv-match' ? 'primary' : 'inherit'} />
                                </ListItemIcon>
                                <ListItemText primary="CV Match" />
                            </ListItemButton>

                            {/* ToR CV Search Button */}
                            <ListItemButton
                                selected={searchMode === 'tor-cv-search'}
                                onClick={() => {
                                    setSearchMode('tor-cv-search');
                                    setSearchQuery('');
                                    setSearchResults([]);
                                    navigate('/tor-cv-search');
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                    <DescriptionIcon fontSize="small" color={searchMode === 'tor-cv-search' ? 'primary' : 'inherit'} />
                                </ListItemIcon>
                                <ListItemText primary="ToR CV Search" />
                            </ListItemButton>

                            {/* ToR Criteria Button */}
                            <ListItemButton
                                selected={searchMode === 'tor-criteria'}
                                onClick={() => {
                                    setSearchMode('tor-criteria');
                                    setSearchQuery('');
                                    setSearchResults([]);
                                    navigate('/tor-criteria');
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                    <AssignmentIcon fontSize="small" color={searchMode === 'tor-criteria' ? 'primary' : 'inherit'} />
                                </ListItemIcon>
                                <ListItemText primary="ToR Criteria" />
                            </ListItemButton>

                            {/* Find Similar CVs Button */}
                            <ListItemButton
                                selected={searchMode === 'find-similar-cvs'}
                                onClick={() => {
                                    setSearchMode('find-similar-cvs');
                                    setSearchQuery('');
                                    setSearchResults([]);
                                    navigate('/find-similar-cvs');
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                    <PersonIcon fontSize="small" color={searchMode === 'find-similar-cvs' ? 'primary' : 'inherit'} />
                                </ListItemIcon>
                                <ListItemText primary="Find Similar CVs" />
                            </ListItemButton>

                            {/* Skills Matrix Button */}
                            <ListItemButton
                                selected={searchMode === 'skills-matrix'}
                                onClick={() => {
                                    setSearchMode('skills-matrix');
                                    setSearchQuery('');
                                    setSearchResults([]);
                                    navigate('/skills-matrix');
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                    <GridOnIcon fontSize="small" color={searchMode === 'skills-matrix' ? 'primary' : 'inherit'} />
                                </ListItemIcon>
                                <ListItemText primary="Skills Matrix" />
                            </ListItemButton>

                            {/* Document Chat Button - Dev Only */}
                            <DevOnlySection userEmail={getUserEmail()}>
                                <ListItemButton
                                    selected={searchMode === 'document-chat'}
                                    onClick={() => {
                                        setSearchMode('document-chat');
                                        setSearchQuery('');
                                        setSearchResults([]);
                                        // Navigate to document chat page
                                        navigate('/document-chat');
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                        <ChatBubbleOutlineIcon fontSize="small" color={searchMode === 'document-chat' ? 'primary' : 'inherit'} />
                                    </ListItemIcon>
                                    <ListItemText primary="Document Chat" />
                                </ListItemButton>
                            </DevOnlySection>

                            {/* CV Creator Button */}
                            <ListItemButton
                                selected={searchMode === 'cv-creator'}
                                onClick={() => {
                                    setSearchMode('cv-creator');
                                    setSearchQuery('');
                                    setSearchResults([]);
                                    navigate('/cv-creator');
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                    <AutoFixHighIcon fontSize="small" color={searchMode === 'cv-creator' ? 'primary' : 'inherit'} />
                                </ListItemIcon>
                                <ListItemText primary="CV Creator" />
                            </ListItemButton>

                            {/* CV Converter Button */}
                            <ListItemButton
                                selected={searchMode === 'cv-converter'}
                                onClick={() => {
                                    setSearchMode('cv-converter');
                                    setSearchQuery('');
                                    setSearchResults([]);
                                    // Navigate to CV converter page
                                    navigate('/cv-converter');
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                    <TransformIcon fontSize="small" color={searchMode === 'cv-converter' ? 'primary' : 'inherit'} />
                                </ListItemIcon>
                                <ListItemText primary="CV Converter" />
                            </ListItemButton>

                        </List>
                    </Box>
                    </SuperAdminOnlySection>

                    {/* Projects Section - Collapsible - Super Admin only */}
                    <SuperAdminOnlySection userEmail={getUserEmail()}>
                        <Box sx={{ mb: 2 }}>
                            {/* Professional Top-Level Projects Button */}
                            <ListItemButton
                                onClick={() => setProjectsSectionOpen(!projectsSectionOpen)}
                                sx={{
                                    mx: 1,
                                    mb: 1,
                                    borderRadius: 2,
                                    backgroundColor: projectsSectionOpen ? 'rgba(34, 184, 167, 0.08)' : 'transparent',
                                    border: '1px solid rgba(255,255,255,0.12)',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: 'rgba(34, 184, 167, 0.12)',
                                        borderColor: 'rgba(34, 184, 167, 0.3)',
                                        transform: 'translateX(2px)'
                                    }
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                    <AccountTreeIcon fontSize="medium" color={projectsSectionOpen ? 'primary' : 'inherit'} />
                                </ListItemIcon>
                                <ListItemText 
                                    primary="Projects" 
                                    primaryTypographyProps={{ 
                                        fontWeight: 600,
                                        fontSize: '0.95rem',
                                        color: projectsSectionOpen ? 'primary.main' : 'text.primary'
                                    }} 
                                />
                                {projectsSectionOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </ListItemButton>

                            {/* Collapsible Projects Items */}
                            <Collapse in={projectsSectionOpen} timeout="auto" unmountOnExit>
                                <List component="nav" dense sx={{ px: 1, pl: 2 }}>
                                    {/* Proposal Search Button */}
                                    <ListItemButton
                                        selected={searchMode === 'proposal'}
                                        onClick={() => {
                                            setSearchMode('proposal');
                                            setSearchQuery('');
                                            setSearchResults([]);
                                            navigate('/');
                                        }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 32 }}>
                                            <DescriptionIcon fontSize="small" color={searchMode === 'proposal' ? 'primary' : 'inherit'} />
                                        </ListItemIcon>
                                        <ListItemText primary="Proposal Search" />
                                    </ListItemButton>

                                    {/* Project Insights Button */}
                                    <ListItemButton
                                        selected={searchMode === 'project-insights'}
                                        onClick={() => {
                                            setSearchMode('project-insights');
                                            navigate('/project-insights');
                                        }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 32 }}>
                                            <InsightsIcon fontSize="small" color={searchMode === 'project-insights' ? 'primary' : 'inherit'} />
                                        </ListItemIcon>
                                        <ListItemText primary="Project Insights" />
                                    </ListItemButton>

                                    {/* Cost Estimator Button */}
                                    <ListItemButton
                                        selected={searchMode === 'cost-estimator'}
                                        onClick={() => {
                                            setSearchMode('cost-estimator');
                                            navigate('/cost-estimator');
                                        }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 32 }}>
                                            <PaymentsIcon fontSize="small" color={searchMode === 'cost-estimator' ? 'primary' : 'inherit'} />
                                        </ListItemIcon>
                                        <ListItemText primary="Cost Estimator" />
                                    </ListItemButton>
                                </List>
                            </Collapse>
                        </Box>
                    </SuperAdminOnlySection>

                    {/* Bid Support Section - Collapsible - Super Admin only */}
                    <SuperAdminOnlySection userEmail={getUserEmail()}>
                        <Box sx={{ mb: 2 }}>
                            {/* Professional Top-Level Bid Support Button */}
                            <ListItemButton
                                onClick={() => setBidSupportSectionOpen(!bidSupportSectionOpen)}
                                sx={{
                                    mx: 1,
                                    mb: 1,
                                    borderRadius: 2,
                                    backgroundColor: bidSupportSectionOpen ? 'rgba(34, 184, 167, 0.08)' : 'transparent',
                                    border: '1px solid rgba(255,255,255,0.12)',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: 'rgba(34, 184, 167, 0.12)',
                                        borderColor: 'rgba(34, 184, 167, 0.3)',
                                        transform: 'translateX(2px)'
                                    }
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                    <BusinessCenterIcon fontSize="medium" color={bidSupportSectionOpen ? 'primary' : 'inherit'} />
                                </ListItemIcon>
                                <ListItemText 
                                    primary="Bid Support" 
                                    primaryTypographyProps={{ 
                                        fontWeight: 600,
                                        fontSize: '0.95rem',
                                        color: bidSupportSectionOpen ? 'primary.main' : 'text.primary'
                                    }} 
                                />
                                {bidSupportSectionOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </ListItemButton>

                            {/* Collapsible Bid Support Items */}
                            <Collapse in={bidSupportSectionOpen} timeout="auto" unmountOnExit>
                                <List component="nav" dense sx={{ px: 1, pl: 2 }}>
                                    {/* Proposal Assistant Button */}
                                    <ListItemButton
                                        selected={searchMode === 'proposal-assistant'}
                                        onClick={() => {
                                            setSearchMode('proposal-assistant');
                                            setSearchQuery('');
                                            setSearchResults([]);
                                            navigate('/proposal-assistant');
                                        }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 32 }}>
                                            <AutoFixHighIcon fontSize="small" color={searchMode === 'proposal-assistant' ? 'primary' : 'inherit'} />
                                        </ListItemIcon>
                                        <ListItemText primary="Proposal Assistant" />
                                    </ListItemButton>
                                </List>
                            </Collapse>
                        </Box>
                    </SuperAdminOnlySection>

                    {/* Travel Section - Collapsible */}
                    <Box sx={{ mb: 2 }}>
                        {/* Professional Top-Level Travel Button */}
                        <ListItemButton
                            onClick={() => setTravelSectionOpen(!travelSectionOpen)}
                            sx={{
                                mx: 1,
                                mb: 1,
                                borderRadius: 2,
                                backgroundColor: travelSectionOpen ? 'rgba(34, 184, 167, 0.08)' : 'transparent',
                                border: '1px solid rgba(255,255,255,0.12)',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    backgroundColor: 'rgba(34, 184, 167, 0.12)',
                                    borderColor: 'rgba(34, 184, 167, 0.3)',
                                    transform: 'translateX(2px)'
                                }
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                                <FlightIcon fontSize="medium" color={travelSectionOpen ? 'primary' : 'inherit'} />
                            </ListItemIcon>
                            <ListItemText 
                                primary="Travel" 
                                primaryTypographyProps={{ 
                                    fontWeight: 600,
                                    fontSize: '0.95rem',
                                    color: travelSectionOpen ? 'primary.main' : 'text.primary'
                                }} 
                            />
                            {travelSectionOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </ListItemButton>

                        {/* Collapsible Travel Items */}
                        <Collapse in={travelSectionOpen} timeout="auto" unmountOnExit>
                            <List component="nav" dense sx={{ px: 1, pl: 2 }}>
                            {/* FCDO Country Risk Button */}
                            <ListItemButton
                                selected={window.location.hash === '#/fcdo-country-risk'}
                                onClick={() => {
                                    navigate('/fcdo-country-risk');
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                    <PublicIcon fontSize="small" color={window.location.hash === '#/fcdo-country-risk' ? 'primary' : 'inherit'} />
                                </ListItemIcon>
                                <ListItemText primary="FCDO Country Risk" />
                            </ListItemButton>

                            {/* Travel Form Button */}
                            <ListItemButton
                                selected={window.location.hash === '#/travel-form'}
                                onClick={() => {
                                    navigate('/travel-form?new=true');
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                    <FlightIcon fontSize="small" color={window.location.hash === '#/travel-form' ? 'primary' : 'inherit'} />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Travel Form"
                                />
                            </ListItemButton>

                            {/* My Travel Button */}
                            <ListItemButton
                                selected={window.location.hash === '#/my-travel'}
                                onClick={() => {
                                    navigate('/my-travel');
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                    <AssignmentIcon fontSize="small" color={window.location.hash === '#/my-travel' ? 'primary' : 'inherit'} />
                                </ListItemIcon>
                                <ListItemText
                                    primary="My Travel"
                                />
                            </ListItemButton>

                            {/* Travel Calendar Button */}
                            <ListItemButton
                                selected={window.location.hash === '#/travel-calendar'}
                                onClick={() => {
                                    navigate('/travel-calendar');
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                    <EventIcon fontSize="small" color={window.location.hash === '#/travel-calendar' ? 'primary' : 'inherit'} />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Travel Calendar"
                                />
                            </ListItemButton>

                            {/* Incident Report Button */}
                            <ListItemButton
                                selected={window.location.hash === '#/travel/incident-report'}
                                onClick={() => {
                                    navigate('/travel/incident-report');
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                    <WarningIcon fontSize="small" color={window.location.hash === '#/travel/incident-report' ? 'primary' : 'inherit'} />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Incident Report"
                                />
                            </ListItemButton>

                            {/* Crisis Log Button */}
                            <ListItemButton
                                selected={window.location.hash === '#/travel/crisis-log'}
                                onClick={() => {
                                    navigate('/travel/crisis-log');
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                    <ReportIcon fontSize="small" color={window.location.hash === '#/travel/crisis-log' ? 'primary' : 'inherit'} />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Crisis Log"
                                />
                            </ListItemButton>

                            {/* Travel Resources Dropdown */}
                            <ListItemButton
                                onClick={(e) => {
                                    const target = e.currentTarget;
                                    setTravelResourcesAnchorEl(travelResourcesAnchorEl ? null : target);
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                    <MenuIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary="Travel Resources" />
                                {travelResourcesAnchorEl ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </ListItemButton>
                            <Menu
                                anchorEl={travelResourcesAnchorEl}
                                open={Boolean(travelResourcesAnchorEl)}
                                onClose={() => setTravelResourcesAnchorEl(null)}
                                anchorOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'left',
                                }}
                            >
                                <MenuItem onClick={() => { navigate('/travel-resources?tab=0'); setTravelResourcesAnchorEl(null); }}>
                                    SP Overseas Travel Page
                                </MenuItem>
                                <MenuItem onClick={() => { navigate('/travel-resources?tab=1'); setTravelResourcesAnchorEl(null); }}>
                                    Travel Framework Guide
                                </MenuItem>
                                <MenuItem onClick={() => { navigate('/travel-resources?tab=2'); setTravelResourcesAnchorEl(null); }}>
                                    Travel Risk Procedure
                                </MenuItem>
                                <MenuItem onClick={() => { navigate('/travel-resources?tab=3'); setTravelResourcesAnchorEl(null); }}>
                                    Crisis Management Plan
                                </MenuItem>
                                <MenuItem onClick={() => { navigate('/travel-resources?tab=4'); setTravelResourcesAnchorEl(null); }}>
                                    Roles and Responsibilities
                                </MenuItem>
                                <MenuItem onClick={() => { 
                                    const link = document.createElement('a');
                                    link.href = '/Employee-Travel-Pack.pdf';
                                    link.target = '_blank';
                                    link.rel = 'noopener noreferrer';
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                    setTravelResourcesAnchorEl(null);
                                }}>
                                    AIG Travel Pack (PDF)
                                </MenuItem>
                            </Menu>
                        </List>
                    </Collapse>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Forms Section - Collapsible */}
                    <Box sx={{ mb: 2 }}>
                        {/* Professional Top-Level Forms Button */}
                        <ListItemButton
                            onClick={() => setFormsSectionOpen(!formsSectionOpen)}
                            sx={{
                                mx: 1,
                                mb: 1,
                                borderRadius: 2,
                                backgroundColor: formsSectionOpen ? 'rgba(34, 184, 167, 0.08)' : 'transparent',
                                border: '1px solid rgba(255,255,255,0.12)',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    backgroundColor: 'rgba(34, 184, 167, 0.12)',
                                    borderColor: 'rgba(34, 184, 167, 0.3)',
                                    transform: 'translateX(2px)'
                                }
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                                <AssignmentIcon fontSize="medium" color={formsSectionOpen ? 'primary' : 'inherit'} />
                            </ListItemIcon>
                            <ListItemText 
                                primary="Forms" 
                                primaryTypographyProps={{ 
                                    fontWeight: 600,
                                    fontSize: '0.95rem',
                                    color: formsSectionOpen ? 'primary.main' : 'text.primary'
                                }} 
                            />
                            {formsSectionOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </ListItemButton>

                        {/* Collapsible Forms Items */}
                        <Collapse in={formsSectionOpen} timeout="auto" unmountOnExit>
                            <List component="nav" dense sx={{ px: 1, pl: 2 }}>
                            {/* Business Case Form Button */}
                            <ListItemButton
                                component={Link}
                                to="/business-case-form"
                                selected={window.location.hash === '#/business-case-form'}
                            >
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                    <BusinessCenterIcon fontSize="small" color={window.location.hash === '#/business-case-form' ? 'primary' : 'inherit'} />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Business Case"
                                />
                            </ListItemButton>

                            {/* My Business Cases Button */}
                            <ListItemButton
                                component={Link}
                                to="/my-business-cases"
                                selected={window.location.hash === '#/my-business-cases'}
                            >
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                    <AssignmentIcon fontSize="small" color={window.location.hash === '#/my-business-cases' ? 'primary' : 'inherit'} />
                                </ListItemIcon>
                                <ListItemText
                                    primary="My Business Cases"
                                />
                            </ListItemButton>

                            {/* PPD Form Button */}
                            <ListItemButton
                                component={Link}
                                to="/ppd-form"
                                selected={window.location.hash === '#/ppd-form'}
                            >
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                    <SchoolIcon fontSize="small" color={window.location.hash === '#/ppd-form' ? 'primary' : 'inherit'} />
                                </ListItemIcon>
                                <ListItemText
                                    primary="PPD Form"
                                />
                            </ListItemButton>

                            {/* My PPD Forms Button */}
                            <ListItemButton
                                component={Link}
                                to="/my-ppd"
                                selected={window.location.hash === '#/my-ppd'}
                            >
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                    <SchoolIcon fontSize="small" color={window.location.hash === '#/my-ppd' ? 'primary' : 'inherit'} />
                                </ListItemIcon>
                                <ListItemText
                                    primary="My PPD Forms"
                                />
                            </ListItemButton>

                            {/* Event Form Button */}
                            <ListItemButton
                                component={Link}
                                to="/event"
                                selected={window.location.hash === '#/event'}
                            >
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                    <EventIcon fontSize="small" color={window.location.hash === '#/event' ? 'primary' : 'inherit'} />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Event"
                                />
                            </ListItemButton>

                            {/* Whistleblower Form Button */}
                            <ListItemButton
                                component={Link}
                                to="/whistleblower"
                                selected={window.location.hash === '#/whistleblower'}
                            >
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                    <ReportIcon fontSize="small" color={window.location.hash === '#/whistleblower' ? 'primary' : 'inherit'} />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Whistleblower"
                                />
                            </ListItemButton>
                        </List>
                        </Collapse>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Admin Section - Collapsible, Admin Only */}
                    <AdminOnlySection userEmail={getUserEmail()}>
                        <Box sx={{ mb: 2 }}>
                            {/* Professional Top-Level Admin Button */}
                            <ListItemButton
                                onClick={() => setAdminSectionOpen(!adminSectionOpen)}
                                sx={{
                                    mx: 1,
                                    mb: 1,
                                    borderRadius: 2,
                                    backgroundColor: adminSectionOpen ? 'rgba(34, 184, 167, 0.08)' : 'transparent',
                                    border: '1px solid rgba(255,255,255,0.12)',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: 'rgba(34, 184, 167, 0.12)',
                                        borderColor: 'rgba(34, 184, 167, 0.3)',
                                        transform: 'translateX(2px)'
                                    }
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                    <BusinessCenterIcon fontSize="medium" color={adminSectionOpen ? 'primary' : 'inherit'} />
                                </ListItemIcon>
                                <ListItemText 
                                    primary="Admin" 
                                    primaryTypographyProps={{ 
                                        fontWeight: 600,
                                        fontSize: '0.95rem',
                                        color: adminSectionOpen ? 'primary.main' : 'text.primary'
                                    }} 
                                />
                                {adminSectionOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </ListItemButton>

                            {/* Collapsible Admin Items */}
                            <Collapse in={adminSectionOpen} timeout="auto" unmountOnExit>
                                <List component="nav" dense sx={{ px: 1, pl: 2 }}>
                                {/* Business Case Admin Button - Restricted Access */}
                                <BusinessCaseAdminOnlySection userEmail={getUserEmail()}>
                                    <ListItemButton
                                        selected={window.location.hash === '#/business-case-admin'}
                                        onClick={() => {
                                            navigate('/business-case-admin');
                                        }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 32 }}>
                                            <BusinessCenterIcon fontSize="small" color={window.location.hash === '#/business-case-admin' ? 'primary' : 'inherit'} />
                                        </ListItemIcon>
                                        <ListItemText primary="Business Case Admin" />
                                    </ListItemButton>
                                </BusinessCaseAdminOnlySection>

                                {/* Events Admin Button - Restricted Access */}
                                <EventsAdminOnlySection userEmail={getUserEmail()}>
                                    <ListItemButton
                                        selected={window.location.hash === '#/events-admin'}
                                        onClick={() => {
                                            navigate('/events-admin');
                                        }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 32 }}>
                                            <EventIcon fontSize="small" color={window.location.hash === '#/events-admin' ? 'primary' : 'inherit'} />
                                        </ListItemIcon>
                                        <ListItemText primary="Events Admin" />
                                    </ListItemButton>
                                </EventsAdminOnlySection>

                                {/* Travel Dashboard Button - Restricted Access */}
                                <TravelAdminOnlySection userEmail={getUserEmail()}>
                                    <ListItemButton
                                        selected={window.location.hash === '#/travel-dashboard'}
                                        onClick={() => {
                                            navigate('/travel-dashboard');
                                        }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 32 }}>
                                            <FlightIcon fontSize="small" color={window.location.hash === '#/travel-dashboard' ? 'primary' : 'inherit'} />
                                        </ListItemIcon>
                                        <ListItemText primary="Travel Dashboard" />
                                    </ListItemButton>
                                </TravelAdminOnlySection>

                                {/* Travel Operations Button - Restricted Access */}
                                <TravelAdminOnlySection userEmail={getUserEmail()}>
                                    <ListItemButton
                                        selected={window.location.hash === '#/travel-operations'}
                                        onClick={() => {
                                            navigate('/travel-operations');
                                        }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 32 }}>
                                            <WarningIcon fontSize="small" color={window.location.hash === '#/travel-operations' ? 'primary' : 'inherit'} />
                                        </ListItemIcon>
                                        <ListItemText primary="Travel Operations" />
                                    </ListItemButton>
                                </TravelAdminOnlySection>

                                {/* PPD Admin Button - Restricted Access */}
                                <PPDAdminOnlySection userEmail={getUserEmail()}>
                                    <ListItemButton
                                        selected={window.location.hash === '#/ppd-admin'}
                                        onClick={() => {
                                            navigate('/ppd-admin');
                                        }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 32 }}>
                                            <SchoolIcon fontSize="small" color={window.location.hash === '#/ppd-admin' ? 'primary' : 'inherit'} />
                                        </ListItemIcon>
                                        <ListItemText primary="PPD Admin" />
                                    </ListItemButton>
                                </PPDAdminOnlySection>
                            </List>
                            </Collapse>
                        </Box>
                    </AdminOnlySection>

                    {/* Reindexing buttons removed - automatic reindexing now handled by backend */}
                </Box>

                {/* Adjust Main's margin based on drawer state */}
                <Main open={drawerOpen}>
                    {/* Outlet for the route content */}
                    <Outlet />
                </Main>
            </div>

            {/* Snackbar for notifications - don't show server connection messages */}
            <Snackbar
                open={snackbarOpen && !snackbarMessage.includes('connect to server')}
                autoHideDuration={snackbarMessage.includes('Exported to') ? 5000 : 6000}
                onClose={handleCloseNotification}
                sx={{
                    '& .MuiSnackbarContent-root': {
                        backgroundColor: snackbarMessage.includes('Exported to') ? '#2e7d32' : undefined, // Green background for export messages
                        fontWeight: snackbarMessage.includes('Exported to') ? 'bold' : undefined,
                    }
                }}
                message={snackbarMessage.replace('EXPORT_NOTIFICATION: ', '')} // Remove prefix if present
            />

            {/* Info/Status Dialog */}
            <Dialog open={infoDialogOpen} onClose={() => setInfoDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>System Status & Information</DialogTitle>
                <DialogContent dividers>
                    {statusInfo ? (
                        <Box>
                            <Typography variant="body1" gutterBottom>
                                <strong>Overall Status:</strong>
                                <Chip label={getStatusDisplay(statusInfo.status).text}
                                    color={getStatusDisplay(statusInfo.status).color as any}
                                    size="small" sx={{ ml: 1 }} />
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                                <strong>Proposals Last Indexed:</strong> {statusInfo.lastProposalIndexTime ? new Date(statusInfo.lastProposalIndexTime).toLocaleString() : 'Never'}
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                                <strong>CVs Last Indexed:</strong> {statusInfo.lastCvIndexTime ? new Date(statusInfo.lastCvIndexTime).toLocaleString() : 'Never'}
                            </Typography>
                            {/* Add more status details if needed */}
                            {statusInfo.errors && statusInfo.errors.length > 0 && (
                                <Box mt={2}>
                                    <Typography variant="subtitle1" color="error">Indexing Errors:</Typography>
                                    <List dense>
                                        {statusInfo.errors.map((err: any, index: number) => (
                                            <ListItem key={index}>
                                                <ListItemText primary={err.filename} secondary={err.error} />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>
                            )}
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setInfoDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            <ReindexProgress job={reindexJob} isVisible={!!reindexJob} />
        </ThemeProvider>
    );
};

function App() {
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
    const [documents, setDocuments] = useState<Document[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isReindexing, setIsReindexing] = useState(false);
    const [reindexOperation, setReindexOperation] = useState<'cv' | 'proposal' | 'all' | null>(null);
    const [searchMode, setSearchMode] = useState<'proposal' | 'cv' | 'document-chat' | 'cv-converter' | 'methodology-builder' | 'methodology-assessment' | 'cv-match' | 'tor-cv-search' | 'find-similar-cvs' | 'skills-matrix' | 'compass' | 'project-insights' | 'cost-estimator' | 'proposal-builder' | 'proposal-assistant'>('cv');
    const [progress, setProgress] = useState<number>(0);
    const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = useState<string>('');
    const [drawerOpen, setDrawerOpen] = useState<boolean>(true);
    const [statusInfo, setStatusInfo] = useState<ProcessingStatus | null>(null);
    const [infoDialogOpen, setInfoDialogOpen] = useState<boolean>(false);
    const [useMixedHits, setUseMixedHits] = useState<boolean>(false);
    const [sortByHits, setSortByHits] = useState<boolean>(false);
    const [cvSortMode, setCvSortMode] = useState<'total' | 'mixed'>('total');
    const [selectedTabs, setSelectedTabs] = useState<{ [key: string]: number }>({});
    const [error, setError] = useState<string | null>(null);
    const [reindexJob, setReindexJob] = useState<any>(null);

    // Add new state for the proposal filter
    const [proposalOutcomeFilter, setProposalOutcomeFilter] = useState<'all' | 'successful' | 'unsuccessful'>('all');

    // Add state for user authentication
    const [userEmail, setUserEmail] = useState<string | null>(null);

    // Add new state for folder filter
    const [folderFilter, setFolderFilter] = useState<string>('all');

    // Add state for Travel Resources dropdown menu
    const [travelResourcesAnchorEl, setTravelResourcesAnchorEl] = useState<null | HTMLElement>(null);

    // Add state for database connection
    const [dbConnectionStatus, setDbConnectionStatus] = useState<{
        connected: boolean;
        message: string;
        timestamp: string;
    }>({ connected: false, message: 'Checking database connection...', timestamp: new Date().toISOString() });

    // Get current location for route-based searchMode sync
    const location = useLocation();

    // Sync searchMode with current route
    useEffect(() => {
        const path = location.pathname;
        if (path === '/document-chat') {
            setSearchMode('document-chat');
        } else if (path === '/cv-converter') {
            setSearchMode('cv-converter');
        } else if (path === '/methodology-builder') {
            setSearchMode('methodology-builder');
        } else if (path === '/methodology-assessment') {
            setSearchMode('methodology-assessment');
        } else if (path === '/cv-match') {
            setSearchMode('cv-match');
        } else if (path === '/tor-cv-search') {
            setSearchMode('tor-cv-search');
        } else if (path === '/find-similar-cvs') {
            setSearchMode('find-similar-cvs');
        } else if (path === '/skills-matrix') {
            setSearchMode('skills-matrix');
        } else if (path === '/compass') {
            setSearchMode('compass');
        } else if (path === '/project-insights') {
            setSearchMode('project-insights');
        } else if (path === '/cost-estimator') {
            setSearchMode('cost-estimator');
        } else if (path === '/proposal-builder') {
            setSearchMode('proposal-builder');
        } else if (path === '/proposal-assistant') {
            setSearchMode('proposal-assistant');
        } else if (path === '/') {
            // On home page, keep current searchMode if it's cv or proposal
            // Otherwise default to cv
            if (searchMode !== 'cv' && searchMode !== 'proposal') {
                setSearchMode('cv');
            }
        }
    }, [location.pathname]);

    // Check database connection and user authentication
    useEffect(() => {
        const checkDatabaseConnection = async () => {
            try {
                const statusResponse = await checkStatus();
                
                if (statusResponse.connectionStatus) {
                    // Update the connection status state
                    setDbConnectionStatus(statusResponse.connectionStatus);

                    // Update the general status info
                    setStatusInfo({
                        totalFiles: statusResponse.totalFiles || 0,
                        processedFiles: statusResponse.processedFiles || 0,
                        failedFiles: statusResponse.failedFiles || 0,
                        status: statusResponse.status || 'Idle',
                        lastProposalIndexTime: statusResponse.lastProposalIndexTime || null,
                        lastCvIndexTime: statusResponse.lastCvIndexTime || null
                    });
                } else {
                    // Fallback if connectionStatus is not available
                    setDbConnectionStatus({
                        connected: statusResponse.status === 'connected',
                        message: statusResponse.status === 'connected' ? 'Database connected' : 'Database connection status unknown',
                        timestamp: new Date().toISOString()
                    });
                    setStatusInfo(statusResponse as ProcessingStatus);
                }
            } catch (error) {
                console.error('Error checking database connection:', error);
                setDbConnectionStatus({
                    connected: false,
                    message: `Database connection check failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
                    timestamp: new Date().toISOString()
                });
                setStatusInfo(null);
                // REMOVED: No longer show snackbar messages for connection issues
                // Only show snackbar message on first connection failure or after successful connection
                // if (dbConnectionStatus.connected) {
                //     setSnackbarMessage('Database connection lost');
                //     setSnackbarOpen(true);
                // }
            }
        };

        const checkUserAuthentication = async () => {
            try {
                const response = await fetch('/.auth/me');
                if (response.ok) {
                    const authData = await response.json();
                    if (authData && authData.clientPrincipal) {
                        setUserEmail(authData.clientPrincipal.userDetails);
                        console.log('✅ User authenticated:', authData.clientPrincipal.userDetails);
                    }
                }
            } catch (error) {
                console.log('❌ User authentication check failed:', error);
            }
        };

        // Call the check functions immediately
        checkDatabaseConnection();
        checkUserAuthentication();

        // Increase frequency to 5 minutes to reduce forced reflows and server load (was 3 minutes)
        const interval = setInterval(checkDatabaseConnection, 300000); // Changed from 180000ms to 300000ms
        return () => clearInterval(interval);
    }, [dbConnectionStatus.connected]); // Add dependency to only show message on state change

    // Fetch initial data and status
    /* // Commented out as fetchDocuments/fetchStatus might not be needed without DocAnalysisPage
    useEffect(() => {
      const fetchInitialData = async () => {
        try {
          await fetchDocuments();
          await fetchStatus();
        } catch (error) {
          console.error('Error fetching initial data:', error);
          setSnackbarMessage('Failed to fetch initial data');
          setSnackbarOpen(true);
        }
      };
      if (searchMode !== 'doc-analysis') {
        fetchInitialData();
      }
    }, [searchMode]);
    */

    const fetchDocuments = async () => {
        try {
            console.log('Skipping document fetch as DocAnalysisPage is removed.');
        } catch (error) {
            console.error('Error fetching documents:', error);
            setSnackbarMessage('Failed to fetch documents');
            setSnackbarOpen(true);
        }
    };

    const fetchStatus = useCallback(async () => {
        try {
            const response = await axios.get(`${SERVER_URL}/status`);
            setStatusInfo(response.data);
            // Remove status fetch notification - too distracting
            // setSnackbarMessage('Status fetched successfully');
            // setSnackbarOpen(true);
        } catch (error) {
            console.error('Error fetching status:', error);
            setStatusInfo(null);
            // Only show critical errors
            setSnackbarMessage('Failed to fetch system status');
            setSnackbarOpen(true);
        }
    }, []);

    // Add debug function to show all search results with folder information
    const debugSearchResults = (results: any[]) => {
        console.log('========== DEBUGGING SEARCH RESULTS ==========');
        console.log(`Total results: ${results.length}`);

        results.forEach((result: any, index: number) => {
            console.log(`Result #${index + 1}:`);
            console.log(`  ID: ${result.id || 'undefined'}`);
            console.log(`  Filename: ${result.filename || 'undefined'}`);
            console.log(`  folderName: ${result.folderName || 'undefined'}`);
            console.log(`  source_location: ${result.source_location || 'undefined'}`);
            console.log(`  associate_name: ${result.associate_name || 'undefined'}`);
            console.log(`  pdfFilename: ${result.pdfFilename || 'undefined'}`);

            // Try to extract folder from source_location
            if (result.source_location) {
                const pathParts = result.source_location.split(/[\/\\]+/);
                console.log(`  Path parts: ${JSON.stringify(pathParts)}`);
                if (pathParts.length > 2) {
                    const extractedFolder = pathParts[pathParts.length - 2];
                    console.log(`  Extracted folder: ${extractedFolder}`);
                } else {
                    console.log(`  Not enough path parts to extract folder`);
                }
            }

            console.log('----------------------------');
        });

        console.log('============================================');
    };

    // Add this debugging call when results are received
    const handleSearch = async () => {
        setIsLoading(true);
        setSearchResults([]);
        setError(null);
        setProgress(0); // Reset progress at the start

        try {
            console.log(`DEBUG - Search request - Query: "${searchQuery}", Mode: ${searchMode}, Mixed Hits: ${useMixedHits}`);

            // Use the working Data API Builder endpoints instead of custom server API
            let results: any[] = [];

            if (searchMode === 'cv') {
                // Use the searchCVs function from api.ts which works with Data API Builder
                const { searchCVs } = await import('./services/api');
                results = await searchCVs(searchQuery, (progress) => {
                    setProgress(progress);
                }, useMixedHits);
            } else if (searchMode === 'proposal') {
                // Use the searchProposals function from api.ts which works with Data API Builder
                const { searchProposals } = await import('./services/api');
                results = await searchProposals(searchQuery, (progress) => {
                    setProgress(progress);
                }, useMixedHits);
            }

            console.log('===== SEARCH RESULTS FROM DATA API BUILDER =====');
            console.log('Raw data structure:', results);
            if (results && results.length > 0) {
                console.log('First result example:', results[0]);
                console.log('First result keys:', Object.keys(results[0]).join(', '));

                // Add our comprehensive debugging
                debugSearchResults(results);
            }

            // Only sort by total hits if mixed hits is false - if mixed hits is true, use the results as returned
            let resultsToUse;
            if (useMixedHits) {
                // Use results directly when mixed hits is enabled
                console.log('Using results as-is (Mixed Hits)');
                resultsToUse = results;
            } else {
                // Sort by total hits when mixed hits is disabled
                console.log('Sorting results by total hits (Standard)');
                resultsToUse = [...results].sort((a, b) => {
                    // Make sure totalHits exists, fallback to 0 if not
                    const hitsA = a.totalHits || 0;
                    const hitsB = b.totalHits || 0;
                    return hitsB - hitsA;
                });
            }

            setSearchResults(resultsToUse);
            console.log(`Found ${resultsToUse.length} results`);

            // Set progress to 100% when search completes successfully
            setProgress(100);
        } catch (error: any) {
            console.error('Search error:', error);
            setError(error.message || 'An error occurred during search');
        } finally {
            // Add a slight delay before setting isLoading to false
            // This gives users time to see the 100% progress
            setTimeout(() => {
                setIsLoading(false);
            }, 500);
        }
    };

    const handleTabChange = (documentId: string, newValue: number) => {
        setSelectedTabs((prev: { [key: string]: number }) => ({
            ...prev,
            [documentId]: newValue,
        }));
    };

    const handleSearchModeChange = (event: React.MouseEvent<HTMLElement>, newMode: 'proposal' | 'cv') => {
        if (newMode !== null) {
            setSearchMode(newMode);
            setSearchQuery('');
            setSearchResults([]);           // Clear results
            setSnackbarMessage('Search mode changed');
            setSnackbarOpen(true);
        }
    };



    // Reindex functions
    const handleReindexProposals = async () => {
        setIsReindexing(true);
        setReindexOperation('proposal');
        setSnackbarMessage('Creating proposal vectorization job...');
        setSnackbarOpen(true);
        setProgress(0);

        try {
            console.log('🔄 Starting proposal vectorization...');

            // Step 1: Create the reindex job
            const triggerResponse = await fetch('/api/TriggerIndexerFunction', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    syncType: 'full',
                    jobTypes: ['proposals']
                })
            });

            console.log('📡 Trigger response status:', triggerResponse.status, triggerResponse.statusText);

            if (!triggerResponse.ok) {
                let errorDetails = `${triggerResponse.status} ${triggerResponse.statusText}`;
                try {
                    const errorBody = await triggerResponse.json();
                    console.error('❌ Trigger error response:', errorBody);
                    errorDetails = errorBody.message || errorBody.error || errorDetails;
                } catch (e) {
                    console.error('❌ Could not parse trigger error response as JSON');
                }
                throw new Error(`Failed to create vectorization job: ${errorDetails}`);
            }

            const triggerResult = await triggerResponse.json();
            console.log('✅ Job creation result:', triggerResult);

            if (!triggerResult.success || triggerResult.jobs.length === 0) {
                throw new Error(triggerResult.message || 'Failed to create vectorization job');
            }

            const jobId = triggerResult.jobs[0].jobId;
            if (!jobId) {
                throw new Error('No job ID returned from job creation');
            }

            setProgress(10);
            setSnackbarMessage(`✅ Proposal vectorization job created: ${jobId}. Processing in background.`);
            setProgress(50);

            // Show success message and let user know processing is happening
            setSnackbarMessage('✅ Proposal vectorization job created and is now processing in background. This may take several minutes for 1000+ documents.');

            // Update status to show job is in progress
            setStatusInfo(prev => {
                const base = prev || { totalFiles: 0, processedFiles: 0, failedFiles: 0, status: 'Idle', lastProposalIndexTime: null, lastCvIndexTime: null };
                return {
                    ...base,
                    status: 'Proposal vectorization job created and processing...'
                };
            });

            // Don't wait for processing to complete - it runs in background
            setProgress(100);

            // Set the reindex job for progress tracking
            setReindexJob({
                job_id: jobId,
                status: 'starting',
                operation: 'proposal',
                created_at: new Date().toISOString()
            });

        } catch (error: any) {
            console.error('❌ Error during proposal vectorization:', error);
            setSnackbarMessage(`❌ Proposal vectorization failed: ${error.message}`);
            setSnackbarOpen(true);
            setIsReindexing(false);
            setReindexJob(null);
        }
        // Note: Not setting isReindexing to false here - let polling handle it
    };

    const handleReindexCVs = async () => {
        setIsReindexing(true);
        setReindexOperation('cv');
        setSnackbarMessage('Creating CV reindex job...');
        setSnackbarOpen(true);
        setProgress(0);

        try {
            console.log('🔄 Starting CV reindex...');

            // Step 1: Create the reindex job
            const triggerResponse = await fetch('/api/TriggerIndexerFunction', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    syncType: 'full',
                    jobTypes: ['cvs']
                })
            });

            console.log('📡 Trigger response status:', triggerResponse.status, triggerResponse.statusText);

            if (!triggerResponse.ok) {
                let errorDetails = `${triggerResponse.status} ${triggerResponse.statusText}`;
                try {
                    const errorBody = await triggerResponse.json();
                    console.error('❌ Trigger error response:', errorBody);
                    errorDetails = errorBody.message || errorBody.error || errorDetails;
                } catch (e) {
                    console.error('❌ Could not parse trigger error response as JSON');
                }
                throw new Error(`Failed to create reindex job: ${errorDetails}`);
            }

            const triggerResult = await triggerResponse.json();
            console.log('✅ Job creation result:', triggerResult);

            if (!triggerResult.success || triggerResult.jobs.length === 0) {
                throw new Error(triggerResult.message || 'Failed to create reindex job');
            }

            const jobId = triggerResult.jobs[0].jobId;
            if (!jobId) {
                throw new Error('No job ID returned from job creation');
            }

            setProgress(10);
            setSnackbarMessage(`✅ CV reindex job created: ${jobId}. Processing in background.`);
            setProgress(50);

            // Show success message and let user know processing is happening
            setSnackbarMessage('✅ CV reindex job created and is now processing in background. This may take several minutes for 1000+ documents.');

            // Update status to show job is in progress
            setStatusInfo(prev => {
                const base = prev || { totalFiles: 0, processedFiles: 0, failedFiles: 0, status: 'Idle', lastProposalIndexTime: null, lastCvIndexTime: null };
                return {
                    ...base,
                    status: 'CV reindex job created and processing...'
                };
            });

            // Don't wait for processing to complete - it runs in background
            setProgress(100);

            // Set the reindex job for progress tracking
            setReindexJob({
                job_id: jobId,
                status: 'starting',
                operation: 'cv',
                created_at: new Date().toISOString()
            });

        } catch (error: any) {
            console.error('❌ Error during CV reindex:', error);
            setSnackbarMessage(`❌ CV reindex failed: ${error.message}`);
            setSnackbarOpen(true);
            setIsReindexing(false);
            setReindexJob(null);
        }
        // Note: Not setting isReindexing to false here - let polling handle it
    };

    const handleCloseNotification = () => {
        setSnackbarOpen(false);
    };

    // Format date for display
    const formatDate = (dateString: string | null) => {
        if (!dateString) return 'Never indexed';

        try {
            const date = new Date(dateString);
            return date.toLocaleString();
        } catch (e) {
            return 'Invalid date';
        }
    };

    // Function to toggle proposal sorting
    const handleSortToggle = () => {
        setSortByHits((prev: boolean) => !prev);
    };

    // Handler for CV sort mode change
    const handleCvSortModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCvSortMode(event.target.checked ? 'mixed' : 'total');
    };

    // Helper to parse query terms for mixed sort
    const getQueryKeywords = (query: string): Set<string> => {
        const terms = new Set<string>();

        // Extract phrases (text in quotes)
        const phraseMatches = query.match(/"([^"]+)"/g) || [];
        for (const phrase of phraseMatches) {
            // Remove the quotes and add to terms
            terms.add(phrase.replace(/"/g, '').toLowerCase());
        }

        // Remove phrases from query for individual term extraction
        const remainingQuery = query.replace(/"[^"]+"/g, '');

        // Extract individual terms
        const individualTerms = remainingQuery
            .toLowerCase()
            .replace(/\band\b|\bor\b|\bnot\b/g, '') // Remove operators
            .split(/\s+/)
            .filter(term => term.length >= 3); // Basic filtering

        // Add individual terms to the set
        for (const term of individualTerms) {
            terms.add(term);
        }

        return terms;
    };

    // Prepare results for display (sorting if needed)
    const displayedResults = useMemo(() => {
        let resultsToSort = [...searchResults];

        // Apply proposal outcome filter if in proposal mode
        if (searchMode === 'proposal' && proposalOutcomeFilter !== 'all') {
            resultsToSort = resultsToSort.filter(result => {
                const displayName = result.displayName || result.filename || '';

                if (proposalOutcomeFilter === 'successful') {
                    // Look for "- S -" pattern in the name
                    return displayName.includes('- S -');
                } else if (proposalOutcomeFilter === 'unsuccessful') {
                    // Look for "- U -" pattern in the name
                    return displayName.includes('- U -');
                }
                return true;
            });
        }

        // Apply folder filter for CV mode
        if (searchMode === 'cv' && folderFilter !== 'all') {
            resultsToSort = resultsToSort.filter(result => {
                const folder = result.folderName ||
                    (result.source_location ?
                        result.source_location.split(/[\/\\]+/).slice(-2, -1)[0] :
                        'Unknown');
                return folder === folderFilter;
            });
        }

        // Only apply local sorting if not already sorted by the server with useMixedHits
        // For proposals, always sort locally
        if (searchMode === 'proposal' && sortByHits) {
            // Proposal sorting: Sort by total hits
            resultsToSort.sort((a, b) => b.totalHits - a.totalHits);
        }
        // For CVs, only sort locally if not using server-side mixed hits sorting
        else if (searchMode === 'cv' && !useMixedHits) {
            // Always sort by total hits for CV mode when not using server mixed hits
            resultsToSort.sort((a, b) => b.totalHits - a.totalHits);
        }

        // Return the filtered and possibly sorted results
        return resultsToSort;
    }, [searchResults, searchMode, sortByHits, useMixedHits, proposalOutcomeFilter, folderFilter]);

    // Use mixed hits checkbox handler
    const handleMixedHitsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.checked;
        console.log(`DEBUG - Mixed Hits toggle changed from ${useMixedHits} to ${newValue}`);
        setUseMixedHits(newValue);

        // No automatic search - user must press search button
        setSnackbarMessage('Mixed Hits setting changed. Press Search to apply.');
        setSnackbarOpen(true);
    };

    // Helper to run search with a specific mixed hits value
    const handleSearchWithMixedHits = async (mixedHitsValue: boolean) => {
        setIsLoading(true);
        setSearchResults([]);
        setError(null);
        setProgress(0); // Reset progress at the start

        try {
            console.log(`DEBUG - Direct search with Mixed Hits: ${mixedHitsValue}`);

            // Use the working Data API Builder endpoints instead of custom server API
            let results: any[] = [];

            if (searchMode === 'cv') {
                // Use the searchCVs function from api.ts which works with Data API Builder
                const { searchCVs } = await import('./services/api');
                results = await searchCVs(searchQuery, (progress) => {
                    setProgress(progress);
                }, mixedHitsValue);
            } else if (searchMode === 'proposal') {
                // Use the searchProposals function from api.ts which works with Data API Builder
                const { searchProposals } = await import('./services/api');
                results = await searchProposals(searchQuery, (progress) => {
                    setProgress(progress);
                }, mixedHitsValue);
            }

            setSearchResults(results);
            console.log(`Found ${results.length} results with Mixed Hits = ${mixedHitsValue}`);

            // Set progress to 100% when search completes successfully
            setProgress(100);
        } catch (error: any) {
            console.error('Search error:', error);
            setError(error.message || 'An error occurred during search');
        } finally {
            setTimeout(() => {
                setIsLoading(false);
            }, 500);
        }
    };

    // Simple notification handler for child components
    const handleSetNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'info') => {
        // TODO: Potentially use severity to change Snackbar appearance
        setSnackbarMessage(message);
        setSnackbarOpen(true);
    };

    // Function to export CV search results to Excel
    const exportToExcel = async () => {
        if (searchResults.length === 0 || searchMode !== 'cv') {
            console.log("Cannot export: No search results or not in CV mode");
            setSnackbarMessage('No CV search results to export');
            setSnackbarOpen(true);
            return;
        }

        try {
            console.log('Exporting CV search results to Excel...');
            console.log('Number of results to export:', searchResults.length);

            // Define file name earlier in case we need it for error reporting
            const fileName = `CV_Search_${searchQuery.replace(/[^a-z0-9]/gi, '_').substring(0, 30)}_${new Date().toISOString().slice(0, 10)}.xlsx`;

            // Use filtered results instead of all results
            const resultsToExport = folderFilter === 'all' ? searchResults : displayedResults;
            console.log('Filtered results to export:', resultsToExport.length);

            try {
                // Extract all unique keywords from the results
                const allKeywords = new Set<string>();
                resultsToExport.forEach(result => {
                    if (!result.termMatches) {
                        console.warn('Result missing termMatches:', result);
                        return;
                    }

                    if (Array.isArray(result.termMatches)) {
                        result.termMatches.forEach(tm => {
                            if (tm.term) allKeywords.add(tm.term);
                        });
                    } else {
                        // If termMatches is an object with term keys
                        Object.keys(result.termMatches).forEach(term => {
                            allKeywords.add(term);
                        });
                    }
                });
                const keywordsArray = Array.from(allKeywords);
                console.log('Extracted keywords:', keywordsArray);

                // Create header row
                const headers = ['Name', 'Folder', ...keywordsArray, 'Total Hits'];
                console.log('Excel headers:', headers);

                // Create data rows
                const rows = resultsToExport.map((result, index) => {
                    try {
                        // Get document name with same logic as CVSearchResult component
                        let documentName = '';
                        if (result.pdfFilename) {
                            documentName = result.pdfFilename.replace(/\.pdf$/i, '');
                        } else if (result.associate_name) {
                            documentName = result.associate_name.replace(/\.pdf$/i, '');
                        } else if (result.source_location) {
                            const pathParts = result.source_location.split(/[\/\\]+/);
                            const lastPart = pathParts[pathParts.length - 1];
                            if (lastPart && lastPart.toLowerCase().endsWith('.pdf')) {
                                documentName = lastPart.replace(/\.pdf$/i, '');
                            }
                        }
                        if (!documentName) {
                            documentName = result.id ? `Document ${result.id}` : 'Unknown Document';
                        }

                        // Get folder name with same logic as CVSearchResult component
                        let folderName = '';
                        if (result.folderName && result.folderName !== 'Unknown Folder') {
                            folderName = result.folderName;
                        } else if (result.source_location) {
                            const pathParts = result.source_location.split(/[\/\\]+/);
                            if (pathParts.length > 2) {
                                folderName = pathParts[pathParts.length - 2];
                            }
                        } else if (result.id && typeof result.id === 'string' && result.id.includes('\\')) {
                            const pathParts = result.id.split('\\');
                            if (pathParts.length >= 2) {
                                folderName = pathParts[0];
                            }
                        }
                        if (!folderName) {
                            folderName = 'Unknown Folder';
                        }

                        // Create a map of term to hit count for this result
                        const termHits = new Map();
                        if (result.termMatches) {
                            if (Array.isArray(result.termMatches)) {
                                result.termMatches.forEach(tm => {
                                    termHits.set(tm.term, tm.hitCount || 0);
                                });
                            } else {
                                // Handle object format
                                Object.entries(result.termMatches).forEach(([term, details]: [string, any]) => {
                                    termHits.set(term, details.hitCount || 0);
                                });
                            }
                        }

                        // Create the row 
                        const row = [
                            documentName,
                            folderName,
                            ...keywordsArray.map(keyword => termHits.get(keyword) || 0),
                            result.totalHits || 0
                        ];

                        return row;
                    } catch (rowError) {
                        console.error(`Error processing result ${index}:`, rowError);
                        // Return a fallback row to prevent the entire export from failing
                        return ['Error processing result', 'Unknown', ...Array(keywordsArray.length).fill(0), 0];
                    }
                });

                console.log('Prepared Excel rows:', rows.length);

                // XLSX processing
                try {
                    console.log('Starting Excel processing...');

                    // Combine headers and rows
                    const data = [headers, ...rows];
                    console.log('Combined data array:', data.length, 'rows');

                    // Create workbook and worksheet with exceljs
                    console.log('Creating workbook and worksheet...');
                    const workbook = new ExcelJS.Workbook();
                    const worksheet = workbook.addWorksheet('CV Search Results');

                    // Add all rows (headers + data)
                    data.forEach((row: any) => {
                        worksheet.addRow(row);
                    });
                    console.log('Worksheet created successfully with', data.length, 'rows');

                // Create the binary data
                console.log('Converting workbook to binary...');
                const buffer = await workbook.xlsx.writeBuffer();
                console.log('Binary conversion successful, size:', buffer.byteLength, 'bytes');

                // Create and trigger download
                console.log('Creating blob and download link...');
                const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                const url = URL.createObjectURL(blob);

                const a = document.createElement('a');
                a.href = url;
                a.download = fileName;

                console.log('Triggering download...');
                document.body.appendChild(a);
                a.click();

                    console.log('Triggering download...');
                    document.body.appendChild(a);
                    a.click();

                    // Clean up
                    setTimeout(() => {
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                        console.log('Download resources cleaned up');
                    }, 100);

                    console.log('Excel export complete');
                    // Don't show any success notification
                } catch (xlsxError: any) {
                    console.error('XLSX processing error:', xlsxError);
                    throw new Error(`XLSX processing failed: ${xlsxError.message}`);
                }
            } catch (dataError: any) {
                console.error('Error preparing data for Excel export:', dataError);
                throw new Error(`Data preparation failed: ${dataError.message}`);
            }
        } catch (error: any) {
            console.error('Export to Excel failed:', error);
            setSnackbarMessage(`Export failed: ${error.message || 'Unknown error'}`);
            setSnackbarOpen(true);

            // Last resort - try a different approach for very simple export
            try {
                console.log('Attempting fallback CSV export...');
                const csvContent = "data:text/csv;charset=utf-8," +
                    searchResults.map(result => `"${result.filename || result.associate_name || 'Unknown'}",${result.totalHits || 0}`).join("\n");

                const encodedUri = encodeURI(csvContent);
                const link = document.createElement("a");
                link.setAttribute("href", encodedUri);
                link.setAttribute("download", "search_results.csv");
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                console.log('Fallback CSV export completed');
            } catch (csvError) {
                console.error('Even fallback CSV export failed:', csvError);
            }
        }
    };

    // Get unique folders from search results for the dropdown
    const uniqueFolders = useMemo(() => {
        if (searchMode !== 'cv' || searchResults.length === 0) return [];

        // Extract all folder names from results
        const folders = new Set<string>();
        searchResults.forEach(result => {
            const folder = result.folderName ||
                (result.source_location ?
                    result.source_location.split(/[\/\\]+/).slice(-2, -1)[0] :
                    'Unknown');
            if (folder && folder.trim() !== '') {
                folders.add(folder);
            }
        });

        // Convert to array and sort alphabetically
        return ['all', ...Array.from(folders)].sort((a, b) => {
            if (a === 'all') return -1; // Keep 'all' as first option
            if (b === 'all') return 1;
            return a.localeCompare(b);
        });
    }, [searchResults, searchMode]);

    // Polling for reindex status
    useEffect(() => {
        if (!reindexJob?.job_id) return;

        const pollStatus = async () => {
            try {
                const response = await fetch(`/api/ReindexStatusFunction?jobId=${reindexJob.job_id}`);
                if (!response.ok) return;
                const data = await response.json();

                if (data.success) {
                    // Batch state updates to reduce forced reflows
                    const updates = {
                        job: {
                            ...reindexJob,
                            ...data.job
                        },
                        isReindexing: isReindexing,
                        reindexOperation: reindexOperation,
                        snackbarMessage: snackbarMessage,
                        snackbarOpen: snackbarOpen
                    };

                    // Update the job with the latest status
                    setReindexJob(updates.job);

                    // Handle job completion
                    if (data.job.status === 'completed' || data.job.status === 'failed') {
                        updates.isReindexing = false;
                        updates.reindexOperation = null;

                        // Show completion message
                        const operation = reindexJob.operation === 'cv' ? 'CV reindex' : 'Proposal vectorization';
                        if (data.job.status === 'completed') {
                            updates.snackbarMessage = `✅ ${operation} completed successfully!`;
                        } else {
                            updates.snackbarMessage = `❌ ${operation} failed. Check logs for details.`;
                        }
                        updates.snackbarOpen = true;

                        // Apply batched updates
                        setIsReindexing(updates.isReindexing);
                        setReindexOperation(updates.reindexOperation);
                        setSnackbarMessage(updates.snackbarMessage);
                        setSnackbarOpen(updates.snackbarOpen);

                        // Hide progress after 5 seconds
                        setTimeout(() => {
                            setReindexJob(null);
                        }, 5000);
                    }
                }
            } catch (error) {
                console.error('Error polling for reindex status:', error);
            }
        };

        const intervalId = setInterval(pollStatus, 5000); // Reduced frequency from 3s to 5s to minimize forced reflows
        return () => clearInterval(intervalId);
    }, [reindexJob?.job_id, reindexJob?.operation, isReindexing, reindexOperation, snackbarMessage, snackbarOpen]);

    // Reindex functions
    const handleReindexAll = async () => {
        setIsReindexing(true);
        setReindexOperation('all');
        setSnackbarMessage('Initiating comprehensive reindexing for CVs and proposals...');
        setSnackbarOpen(true);
        setProgress(0);

        try {
            console.log('🔄 Starting CV and Proposal reindex...');

            // Step 1: Create both jobs
            const triggerResponse = await fetch('/api/TriggerIndexerFunction', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    syncType: 'full',
                    jobTypes: ['cvs', 'proposals']
                })
            });

            console.log('📡 Trigger response status:', triggerResponse.status);

            // Professional handling: Even if API returns error, jobs may still be created
            if (!triggerResponse.ok) {
                console.log('⚠️ API returned error, but jobs may still be processing');
                setSnackbarMessage('Reindexing jobs have been initiated. You will receive a detailed email report upon completion.');
                setProgress(25);

                // Continue with a simplified success flow
                setTimeout(() => {
                    setProgress(100);
                    setSnackbarMessage('Reindexing process initiated successfully. Check your email for completion status.');
                    setIsReindexing(false);
                    setReindexOperation(null);
                }, 2000);
                return;
            }

            const triggerResult = await triggerResponse.json();
            console.log('✅ Trigger result:', triggerResult);

            if (!triggerResult.success || !triggerResult.jobs || triggerResult.jobs.length === 0) {
                throw new Error('No reindex jobs were created');
            }

            // Use the first job for status tracking (both will be processed)
            const firstJobId = triggerResult.jobs[0].jobId;
            setSnackbarMessage(`Reindexing jobs created successfully. Tracking progress...`);
            setProgress(25);

            // Step 2: Poll for completion
            let completed = false;
            let attempts = 0;
            const maxAttempts = 60; // 10 minutes max

            while (!completed && attempts < maxAttempts) {
                await new Promise(resolve => setTimeout(resolve, 10000)); // Wait 10 seconds
                attempts++;

                try {
                    const statusResponse = await fetch(`/api/ReindexStatusFunction?jobId=${firstJobId}`);
                    if (statusResponse.ok) {
                        const statusResult = await statusResponse.json();
                        console.log(`📊 Status check ${attempts}:`, statusResult);

                        if (statusResult.status === 'completed' || statusResult.status === 'failed') {
                            completed = true;
                            setProgress(100);

                            if (statusResult.status === 'completed') {
                                setSnackbarMessage('Comprehensive reindexing completed successfully! Check your email for detailed results.');
                            } else {
                                setSnackbarMessage('Reindexing process completed. Check your email for detailed results and any issues.');
                            }
                        } else {
                            // Update progress based on attempt
                            const progressPercent = Math.min(25 + (attempts * 2), 95);
                            setProgress(progressPercent);
                            setSnackbarMessage(`Reindexing in progress... (${Math.round(progressPercent)}%)`);
                        }
                    }
                } catch (statusError) {
                    console.log('Status check failed:', statusError);
                    // Continue polling despite status check failures
                }
            }

            if (!completed) {
                setProgress(100);
                setSnackbarMessage('Reindexing is taking longer than expected. You will receive an email when complete.');
            }

        } catch (error) {
            console.error('❌ Error during reindex all:', error);
            setSnackbarMessage('Reindexing jobs have been initiated. You will receive an email confirmation shortly.');
            setProgress(100);
        } finally {
            setIsReindexing(false);
            setReindexOperation(null);
        }
    };

    return (
        <Routes>
            <Route path="/" element={
                <MainLayout
                    drawerOpen={drawerOpen}
                    setDrawerOpen={setDrawerOpen}
                    statusInfo={statusInfo}
                    isLoading={isLoading}
                    isReindexing={isReindexing}
                    handleReindexCVs={handleReindexCVs}
                    handleReindexProposals={handleReindexProposals}
                    handleReindexAll={handleReindexAll}
                    setInfoDialogOpen={setInfoDialogOpen}
                    infoDialogOpen={infoDialogOpen}
                    searchMode={searchMode}
                    setSearchMode={setSearchMode}
                    setSearchQuery={setSearchQuery}
                    setSearchResults={setSearchResults}
                    formatDate={formatDate}
                    snackbarOpen={snackbarOpen}
                    snackbarMessage={snackbarMessage}
                    handleCloseNotification={handleCloseNotification}
                    setSnackbarMessage={setSnackbarMessage}
                    setSnackbarOpen={setSnackbarOpen}
                    dbConnectionStatus={dbConnectionStatus}
                    userEmail={userEmail}
                    reindexJob={reindexJob}
                    travelResourcesAnchorEl={travelResourcesAnchorEl}
                    setTravelResourcesAnchorEl={setTravelResourcesAnchorEl}
                >
                    <Outlet />
                </MainLayout>
            }>
                {/* Default route redirects to main search page */}
                <Route index element={
                    <SearchPageComponent
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        searchResults={searchResults}
                        setSearchResults={setSearchResults}
                        isLoading={isLoading}
                        setIsLoading={setIsLoading}
                        isReindexing={isReindexing}
                        reindexOperation={reindexOperation}
                        searchMode={searchMode}
                        progress={progress}
                        setProgress={setProgress}
                        snackbarMessage={snackbarMessage}
                        error={error}
                        setError={setError}
                        handleSearch={handleSearch}
                        handleSetNotification={handleSetNotification}
                        displayedResults={displayedResults}
                        uniqueFolders={uniqueFolders}
                        folderFilter={folderFilter}
                        setFolderFilter={setFolderFilter}
                        exportToExcel={exportToExcel}
                        proposalOutcomeFilter={proposalOutcomeFilter}
                        setProposalOutcomeFilter={setProposalOutcomeFilter}
                        useMixedHits={useMixedHits}
                        handleMixedHitsChange={handleMixedHitsChange}
                    />
                } />

                {/* Chat page route */}
                <Route path="/chat/:documentType/:documentIdentifier" element={<ChatPage />} />

                {/* Document viewer route */}
                <Route path="/document/:documentType/:filename" element={<DocumentViewer />} />

                {/* Document Chat page route */}
                <Route path="/document-chat" element={<DocumentChatPage />} />

                {/* CV Creator page route */}
                <Route path="/cv-creator" element={<CVCreatorPage />} />

                {/* CV Converter page route */}
                <Route path="/cv-converter" element={<CVConverterPage />} />

                {/* FCDO Country Risk page route */}
                <Route path="/fcdo-country-risk" element={<FCDOCountryRiskPage />} />

                {/* Travel Form page route */}
                <Route path="/travel-form" element={<TravelFormPage />} />

                {/* My Travel page route */}
                <Route path="/my-travel" element={<TravelListPage />} />

                {/* Travel Dashboard page route */}
                <Route path="/travel-dashboard" element={<TravelDashboardPage />} />

                {/* Travel Operations page route (admin only) */}
                <Route path="/travel-operations" element={<TravelOperationsPage />} />

                {/* Travel Calendar page route */}
                <Route path="/travel-calendar" element={<TravelCalendarPage />} />

                {/* Travel Resources page route */}
                <Route path="/travel-resources" element={<TravelResourcesPage />} />

                {/* Incident Report route - blank form only */}
                <Route path="/travel/incident-report" element={<IncidentReportPage />} />

                {/* Crisis Log route - blank form only */}
                <Route path="/travel/crisis-log" element={<CrisisLogPage />} />

                {/* Business Case routes */}
                <Route path="/business-case-form" element={<BusinessCaseFormPage />} />
                <Route path="/my-business-cases" element={<MyBusinessCasesPage />} />
                <Route path="/business-case-admin" element={<BusinessCaseAdminPage />} />

                {/* Events Admin route */}
                <Route path="/events-admin" element={<EventsAdminPage />} />

                {/* PPD routes */}
                <Route path="/ppd-form" element={<PPDFormPage />} />
                <Route path="/my-ppd" element={<MyPPDPage />} />
                <Route path="/ppd-admin" element={<PPDAdminPage />} />

                {/* Methodology Builder route - Admin only */}
                <Route path="/methodology-builder" element={<MethodologyBuilderPage />} />
                <Route path="/methodology-assessment" element={<MethodologyAssessmentPage />} />
                
                {/* Proposal Builder route - Multi-section proposal generation */}
                <Route path="/proposal-builder" element={<ProposalBuilderPage />} />
                
                {/* Proposal Assistant - AI-powered ToR analysis with methodology + CV matching */}
                <Route path="/proposal-assistant" element={<ProposalAssistantPage />} />

                {/* Event Form page route */}
                <Route path="/event" element={<EventFormPage />} />

                {/* EOT Anonymous Message page route */}
                <Route path="/eot-anonymous-message" element={<EOTAnonymousMessagePage />} />

                {/* Whistleblower Form page route */}
                <Route path="/whistleblower" element={<WhistleblowerFormPage />} />

                {/* CV Match page route */}
                <Route path="/cv-match" element={<CVMatchPage />} />
                
                {/* ToR CV Search page route */}
                <Route path="/tor-cv-search" element={<TorCVSearchPage />} />

                {/* ToR Criteria page route */}
                <Route path="/tor-criteria" element={<TorCriteriaPage />} />

                {/* Find Similar CVs page route */}
                <Route path="/find-similar-cvs" element={<FindSimilarCVsPage />} />

                {/* Skills Matrix - Score CVs against TOR criteria */}
                <Route path="/skills-matrix" element={<SkillsMatrixPage />} />

                {/* Compass BD Intelligence */}
                <Route path="/compass" element={<CompassDashboardPage />} />
                <Route path="/compass-test" element={<CompassTestPage />} />

                {/* Project Insights - Search past projects */}
                <Route path="/project-insights" element={<ProjectInsightsPage />} />

                {/* Cost Estimator - Standalone project costing tool */}
                <Route path="/cost-estimator" element={<CostEstimatorPage />} />

                {/* Catch all other routes and redirect to home */}
                <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
        </Routes>
    );
}

export default App;